import { useEffect, useMemo, useRef, useState, Fragment } from "react";
import api from "@/lib/api";
import { downloadXlsx } from "@/lib/download";
import { Plus, Download, Copy, Search, Trash2, Mail, Loader2, Key, Pencil, Eye, EyeOff, CheckCircle2, AlertTriangle, ChevronDown, ChevronRight, TrendingUp, TrendingDown, Minus, FileText, Megaphone, Send, X, Users, Handshake, StickyNote, CalendarClock, BellRing, MessageSquare, Ban, Star, ArrowUpRight, ArrowDownRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { ReviewsAdminModal } from "@/components/ReviewsAdminModal";

const STATUS_LABELS = {
  available: { label: "Disponible", cls: "bg-emerald-100 text-emerald-700" },
  activated: { label: "Activada", cls: "bg-blue-100 text-blue-700" },
  active: { label: "En uso", cls: "bg-slate-900 text-white" },
  cancelled: { label: "Cancelada", cls: "bg-rose-100 text-rose-700" },
};

const TYPE_LABELS = {
  single_use: "Uso único",
  subscription_monthly: "Suscripción · mensual",
  subscription_annual: "Suscripción · anual",
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "—"; }
}

const INACTIVITY_THRESHOLD_DAYS = 30;

function daysSince(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

function formatPeriod(start, end) {  // Devuelve una etiqueta corta del mes/rango cubierto por un estudio.
  // Si start y end son del mismo mes → "ene 2026". Si abarcan varios → "ene → mar 2026".
  const parse = (s) => {
    if (!s) return null;
    try {
      const d = new Date(s);
      if (isNaN(d.getTime())) return null;
      return d;
    } catch { return null; }
  };
  const a = parse(start);
  const b = parse(end);
  const fmt = (d) => d.toLocaleDateString("es-ES", { month: "short", year: "numeric" });
  if (a && b) {
    if (a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()) return fmt(a);
    return `${a.toLocaleDateString("es-ES", { month: "short" })} → ${fmt(b)}`;
  }
  return fmt(a || b) || "—";
}

// Submenú de licencias por situación comercial. `amount` suma el importe
// facturado del grupo para poder ver de un vistazo lo pendiente de cobro.
const BRAND_FILTERS = [
  { id: "all", label: "Todas", color: "#0f172a" },
  { id: "farmaclass", label: "FarmaClass", color: "#0f172a" },
  { id: "sandoz", label: "Sandoz", color: "#1F58B0" },
  { id: "normon", label: "Normon", color: "#001E62" },
  { id: "aurovitas", label: "Aurovitas", color: "#3B3FA3" },
];

const LICENSE_TABS = [
  { id: "all", label: "Todas", match: () => true },
  { id: "unused", label: "Sin utilizar", match: (l) => l.status === "available" },
  { id: "in_use", label: "En uso", match: (l) => l.status === "active" || l.status === "activated" },
  { id: "unbilled", label: "Sin facturar", match: (l) => !l.billed && l.status !== "available" && l.status !== "cancelled" },
  { id: "billed_unpaid", label: "Facturadas y sin cobrar", match: (l) => l.billed && !l.paid, amount: true },
  { id: "billed_paid", label: "Facturadas y cobradas", match: (l) => l.billed && l.paid, amount: true },
  { id: "inactive", label: "Inactivas", match: (l) => l.status === "cancelled" },
];

// Tarifas oficiales (PAYMENT_PACKAGES del backend): se usan como estimación
// cuando la licencia está facturada pero sin importe informado a mano.
const LICENSE_TARIFF = { single_use: 120, subscription_monthly: 30, subscription_annual: 275 };

const licAmount = (l) =>
  Number(l.billed_amount || l.amount || 0) || LICENSE_TARIFF[l.license_type] || 0;

const licAmountIsEstimated = (l) => !Number(l.billed_amount || l.amount || 0) && licAmount(l) > 0;

export default function LicensesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [count, setCount] = useState(10);
  const [prefix, setPrefix] = useState("FC");
  const [createSendEmail, setCreateSendEmail] = useState("");
  const [createPharmacy, setCreatePharmacy] = useState("");
  const [creating, setCreating] = useState(false);
  const [createdResult, setCreatedResult] = useState(null);
  const [filter, setFilter] = useState("");
  const [tab, setTab] = useState("all");
  const [brandFilter, setBrandFilter] = useState("all"); // all | farmaclass | sandoz | normon | aurovitas
  const [createBrand, setCreateBrand] = useState(""); // "" | "sandoz" (marca blanca)
  const [includeTest, setIncludeTest] = useState(false);
  const tableScrollRef = useRef(null);
  const [detailWidth, setDetailWidth] = useState(0);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const measure = () => setDetailWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const [sendingCode, setSendingCode] = useState(null);
  const [reminderModal, setReminderModal] = useState(null);
  const [reminderSending, setReminderSending] = useState(false);
  const [msgModal, setMsgModal] = useState(null);
  const [msgRecipient, setMsgRecipient] = useState("");
  const [msgSubject, setMsgSubject] = useState("");
  const [msgBody, setMsgBody] = useState("");
  const [msgAttachPdf, setMsgAttachPdf] = useState(false);
  const [msgSending, setMsgSending] = useState(false);

  // Modals
  const [sendModal, setSendModal] = useState(null); // {code}
  const [sendEmail, setSendEmail] = useState("");
  const [regenModal, setRegenModal] = useState(null); // {code, result?}
  const [regenLoading, setRegenLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [emailModal, setEmailModal] = useState(null); // {code, currentEmail}
  const [pharmaModal, setPharmaModal] = useState(null); // {code}
  const [pharmaForm, setPharmaForm] = useState({ pharmacy_name: "", phone: "", province: "" });
  const [pharmaProvinces, setPharmaProvinces] = useState([]);
  const [pharmaSaving, setPharmaSaving] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);

  // Panel expandible con resumen consultoría por licencia
  const [expandedCode, setExpandedCode] = useState(null);
  const [summaryCache, setSummaryCache] = useState({}); // {code: {loading, data, error}}
  const [showPwFor, setShowPwFor] = useState(null); // código con contraseña visible en el resumen
  const [pdfModal, setPdfModal] = useState(null); // {code, pharmacy_name, email}
  const [pdfNotes, setPdfNotes] = useState("");
  const [pdfRecs, setPdfRecs] = useState("");
  const [pdfNextVisit, setPdfNextVisit] = useState("");
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [pdfSendEmail, setPdfSendEmail] = useState(false);
  const [pdfRecipient, setPdfRecipient] = useState("");
  const [pdfCustomMessage, setPdfCustomMessage] = useState("");
  const [pdfCustomSubject, setPdfCustomSubject] = useState("");
  const [pdfSending, setPdfSending] = useState(false);

  // Release notes (Informe de mejoras)
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [releasePreview, setReleasePreview] = useState(null); // { versions, latest_title, preview_html }
  const [releaseLoading, setReleaseLoading] = useState(false);
  const [releaseScope, setReleaseScope] = useState("active_users");
  const [releaseTestEmail, setReleaseTestEmail] = useState("");
  const [releaseCustomIntro, setReleaseCustomIntro] = useState("");
  const [releaseSending, setReleaseSending] = useState(false);
  const [releaseResult, setReleaseResult] = useState(null);
  const [releaseRecipients, setReleaseRecipients] = useState(null); // {count, sample}
  const [releaseLastSent, setReleaseLastSent] = useState(null); // {sent_at, sent_up_to_version, new_versions_count, suggested_since_date}
  const [releaseOnlyNew, setReleaseOnlyNew] = useState(true); // iter-74: sólo enviar novedades posteriores al último envío
  const [releaseSinceDate, setReleaseSinceDate] = useState(""); // iter-75: fecha desde (ISO YYYY-MM-DD)
  // iter-76: modal de oferta de renovación por cliente
  const [renewalModal, setRenewalModal] = useState(null); // { user_id, pharmacy_name, email, license_code }
  const [renewalPreview, setRenewalPreview] = useState(null); // { preview_html, recipient, first_access, studies_count }
  const [renewalStats, setRenewalStats] = useState(null); // { total_sends, total_opens, last_send_at, last_open_at, sends[] }
  const [renewalLoading, setRenewalLoading] = useState(false);
  const [renewalSending, setRenewalSending] = useState(false);

  // Estudios duplicados por farmacia+periodo
  const [duplicates, setDuplicates] = useState(null); // { total_groups, groups }
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);
  const [duplicatesDeleting, setDuplicatesDeleting] = useState(null); // disp_id en curso

  const loadDuplicates = () => {
    api.get("/admin/duplicate-studies")
      .then((r) => setDuplicates(r.data))
      .catch(() => setDuplicates({ total_groups: 0, groups: [] }));
  };
  useEffect(() => { loadDuplicates(); }, []);

  const deleteDuplicateStudy = async (dispId, label) => {
    if (!window.confirm(`¿Borrar el estudio "${label}"? Esta acción no se puede deshacer.`)) return;
    setDuplicatesDeleting(dispId);
    try {
      await api.delete(`/admin/dispensations/${dispId}`);
      toast.success("Estudio duplicado eliminado");
      loadDuplicates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al borrar");
    } finally {
      setDuplicatesDeleting(null);
    }
  };

  const openReleaseModal = async () => {
    setShowReleaseModal(true);
    setReleaseResult(null);
    // Recargamos SIEMPRE (sin cache) para que el modal refleje cualquier mejora
    // añadida entre aperturas — solo audiencia usuario, no admin.
    setReleaseLoading(true);
    try {
      const { data } = await api.get("/admin/release-notes", {
        params: { include_admin: false, _ts: Date.now() },
      });
      setReleasePreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando preview");
    } finally {
      setReleaseLoading(false);
    }
    // Cargar destinatarios en paralelo
    try {
      const { data } = await api.get("/admin/release-notes/recipients", { params: { scope: "active_users" } });
      setReleaseRecipients(data);
    } catch (_) { /* silencioso */ }
    // Cargar último envío para saber cuántas mejoras nuevas hay pendientes
    refreshLastSent();
  };

  const refreshLastSent = async () => {
    try {
      const { data } = await api.get("/admin/release-notes/last-sent", { params: { _ts: Date.now() } });
      setReleaseLastSent(data);
      // Iter-75: prellenar el date-picker con la fecha del último envío (o 1º del mes)
      if (data?.suggested_since_date && !releaseSinceDate) {
        setReleaseSinceDate(data.suggested_since_date);
      }
    } catch (_) { setReleaseLastSent(null); }
  };

  const toggleAdminView = async (nextInclude) => {
    setReleaseLoading(true);
    try {
      const { data } = await api.get("/admin/release-notes", { params: { include_admin: nextInclude } });
      setReleasePreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando preview");
    } finally {
      setReleaseLoading(false);
    }
  };

  const refreshRecipients = async (scope) => {
    if (scope !== "active_users" && scope !== "all_licensees") {
      setReleaseRecipients(null);
      return;
    }
    try {
      const { data } = await api.get("/admin/release-notes/recipients", { params: { scope } });
      setReleaseRecipients(data);
    } catch (_) { /* silencioso */ }
  };

  const sendReleaseNotes = async (dryRun = false) => {
    setReleaseSending(true);
    setReleaseResult(null);
    try {
      // Iter-74/75: por defecto enviamos TODAS las mejoras posteriores al último
      // envío. Si el admin activa el date-picker, sólo las de fecha >= since_date.
      const maxV = releaseOnlyNew
        ? Math.max(1, (releaseLastSent?.new_versions_count || 0) || 999)
        : 999;
      const payload = {
        scope: releaseScope,
        dry_run: dryRun,
        max_versions: maxV,
      };
      if (releaseSinceDate) {
        payload.since_date = releaseSinceDate;
      }
      if (releaseScope === "test") {
        if (!releaseTestEmail.includes("@")) {
          toast.error("Indica un email de prueba válido");
          setReleaseSending(false);
          return;
        }
        payload.test_email = releaseTestEmail.trim();
      }
      if (releaseCustomIntro.trim()) {
        payload.intro_html = `<p style='margin:0 0 22px;font-size:15px;color:#334155;line-height:1.6;'>${releaseCustomIntro.trim().replace(/\n/g, "<br/>")}</p>`;
      }
      const { data } = await api.post("/admin/release-notes/send", payload);
      setReleaseResult(data);
      if (dryRun) {
        toast.info(`Preview OK · ${data.recipients_count} destinatarios · ${maxV} versiones`);
      } else {
        toast.success(`Enviado a ${data.sent}/${data.recipients_count} destinatarios (${maxV} versiones)`);
        // Refrescamos el "último enviado" para que el modal muestre el nuevo estado
        refreshLastSent();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error enviando informe");
    } finally {
      setReleaseSending(false);
    }
  };

  const toggleExpand = (code) => {
    if (expandedCode === code) {
      setExpandedCode(null);
      return;
    }
    setExpandedCode(code);
    if (!summaryCache[code]) {
      setSummaryCache((s) => ({ ...s, [code]: { loading: true } }));
      api.get(`/admin/licenses/${code}/consultancy-summary`)
        .then((r) => setSummaryCache((s) => ({ ...s, [code]: { data: r.data, loading: false } })))
        .catch((e) => setSummaryCache((s) => ({ ...s, [code]: { error: e?.response?.data?.detail || "Error", loading: false } })));
    }
  };

  const sendConsultancyPdf = async () => {
    if (!pdfModal) return;
    setPdfSending(true);
    try {
      const { data } = await api.post(
        `/admin/licenses/${pdfModal.code}/consultancy-pdf`,
        {
          notes: pdfNotes,
          recommendations: pdfRecs,
          next_visit: pdfNextVisit,
          send_email: true,
          recipient_email: pdfRecipient,
          custom_message: pdfCustomMessage,
          custom_subject: pdfCustomSubject,
        },
      );
      toast.success(`Email enviado a ${data.sent_to}`);
      setPdfModal(null);
      setPdfNotes(""); setPdfRecs(""); setPdfNextVisit("");
      setPdfSendEmail(false); setPdfRecipient("");
      setPdfCustomMessage(""); setPdfCustomSubject("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error enviando el PDF");
    } finally {
      setPdfSending(false);
    }
  };

  const downloadConsultancyPdf = async () => {
    if (!pdfModal) return;
    setPdfDownloading(true);
    try {
      const response = await api.post(
        `/admin/licenses/${pdfModal.code}/consultancy-pdf`,
        { notes: pdfNotes, recommendations: pdfRecs, next_visit: pdfNextVisit },
        { responseType: "blob" },
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      link.download = `consultoria_${pdfModal.code}_${today}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF descargado");
      setPdfModal(null);
      setPdfNotes("");
      setPdfRecs("");
      setPdfNextVisit("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error generando PDF");
    } finally {
      setPdfDownloading(false);
    }
  };

  const load = () => {
    setLoading(true);
    api.get("/admin/licenses", { params: { include_test: includeTest } })
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, [includeTest]);

  const loadPendingReviews = () => {
    api.get("/admin/reviews")
      .then((r) => setPendingReviews(r.data.pending || 0))
      .catch(() => {});
  };
  useEffect(loadPendingReviews, []);

  const askReview = async (code, emailOverride) => {
    setReviewBusy(code);
    try {
      const { data } = await api.post(`/admin/licenses/${code}/review-request`,
        emailOverride ? { email: emailOverride } : {});
      toast.success(`Enlace de reseña enviado a ${data.sent_to}`);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "No se pudo enviar la petición de reseña";
      if (e?.response?.status === 400 && !emailOverride) {
        const typed = window.prompt(`${detail}\n\nEmail del cliente:`, "");
        if (typed && typed.includes("@")) {
          setReviewBusy(null);
          return askReview(code, typed.trim());
        }
      }
      toast.error(detail);
    } finally {
      setReviewBusy(null);
    }
  };

  const [sendRegenerate, setSendRegenerate] = useState(false);
  const sendCredentials = async () => {
    if (!sendModal) return;
    const to = sendEmail.trim();
    if (!to || !to.includes("@")) {
      toast.error("Email destino no válido");
      return;
    }
    setSendingCode(sendModal.code);
    try {
      const { data } = await api.post(`/admin/licenses/${sendModal.code}/send-credentials`, { email: to, regenerate: sendRegenerate });
      toast.success(`Credenciales ${data.regenerated ? "regeneradas y " : ""}enviadas a ${data.sent_to}`);
      setSendModal(null);
      setSendEmail("");
      setSendRegenerate(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error enviando email");
    } finally {
      setSendingCode(null);
    }
  };

  // Email libre del consultor al cliente (con o sin informe PDF adjunto)
  const sendClientMessage = async () => {
    if (!msgModal) return;
    if (!msgBody.trim()) { toast.error("Escribe el mensaje"); return; }
    setMsgSending(true);
    try {
      const { data } = msgAttachPdf
        ? await api.post(`/admin/licenses/${msgModal.code}/consultancy-pdf`, {
            send_email: true,
            recipient_email: msgRecipient,
            custom_subject: msgSubject,
            custom_message: msgBody,
          })
        : await api.post(`/admin/licenses/${msgModal.code}/send-message`, {
            recipient_email: msgRecipient,
            subject: msgSubject,
            message: msgBody,
          });
      toast.success(`Email enviado a ${data.sent_to}`);
      setMsgModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar el email");
    } finally {
      setMsgSending(false);
    }
  };

  // Email de recordatorio por días sin entrar (reutiliza el envío de usuarios)
  const sendInactivityReminder = async () => {
    if (!reminderModal) return;
    setReminderSending(true);
    try {
      const { data } = await api.post(`/admin/users/${reminderModal.user_id}/send-reminder`);
      toast.success(`Recordatorio enviado a ${data.sent_to}`);
      setReminderModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar el recordatorio");
    } finally {
      setReminderSending(false);
    }
  };


  const sendCredentialsAuto = async (item) => {
    setSendingCode(item.code);
    try {
      // Reenvío automático: intenta sin regenerar primero (más seguro para el
      // cliente que ya tiene su contraseña). Si la licencia está activada
      // (409), abrimos el modal para que el admin decida.
      const { data } = await api.post(`/admin/licenses/${item.code}/send-credentials`, { regenerate: false });
      toast.success(`Credenciales reenviadas a ${data.sent_to}`);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      const status = e?.response?.status;
      if (status === 400 && detail.includes("automáticamente")) {
        setSendModal({ code: item.code });
        setSendEmail(item.credentials_sent_to || "");
        setSendRegenerate(false);
        toast.info("No se pudo resolver el email automáticamente. Indícalo abajo.");
      } else if (status === 409) {
        // Licencia activada: hay que regenerar. Abrimos modal con el flag activado.
        setSendModal({ code: item.code });
        setSendEmail(item.credentials_sent_to || "");
        setSendRegenerate(true);
        toast.info("La licencia ya está activada. Marca «Regenerar contraseña» para enviar una nueva.");
      } else {
        toast.error(detail || "Error reenviando email");
      }
    } finally {
      setSendingCode(null);
    }
  };

  const regeneratePassword = async () => {
    if (!regenModal) return;
    setRegenLoading(true);
    try {
      const { data } = await api.post(`/admin/licenses/${regenModal.code}/regenerate-password`);
      setRegenModal({ ...regenModal, result: data });
      setShowPw(true);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error regenerando contraseña");
    } finally {
      setRegenLoading(false);
    }
  };

  const saveClientEmail = async () => {
    if (!emailModal) return;
    const to = emailInput.trim().toLowerCase();
    if (!to || !to.includes("@")) {
      toast.error("Email no válido");
      return;
    }
    setEmailSaving(true);
    try {
      await api.patch(`/admin/licenses/${emailModal.code}/client-email`, { email: to });
      toast.success("Email del cliente actualizado");
      setEmailModal(null);
      setEmailInput("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando email");
    } finally {
      setEmailSaving(false);
    }
  };

  const openPharmaModal = async (code) => {
    setPharmaModal({ code });
    setPharmaForm({ pharmacy_name: "", phone: "", province: "" });
    setPharmaProvinces([]);
    try {
      const { data } = await api.get(`/admin/licenses/${code}/pharmacy-details`);
      setPharmaForm({
        pharmacy_name: data.pharmacy_name || "",
        phone: data.phone || "",
        province: data.province || "",
      });
      setPharmaProvinces(data.provinces_available || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron cargar los datos");
      setPharmaModal(null);
    }
  };

  const savePharmaDetails = async () => {
    if (!pharmaModal) return;
    if (!pharmaForm.pharmacy_name.trim()) {
      toast.error("El nombre de la farmacia no puede estar vacío");
      return;
    }
    setPharmaSaving(true);
    try {
      await api.patch(`/admin/licenses/${pharmaModal.code}/pharmacy-details`, {
        pharmacy_name: pharmaForm.pharmacy_name.trim(),
        phone: pharmaForm.phone.trim(),
        province: pharmaForm.province.trim(),
      });
      toast.success("Datos de la farmacia actualizados");
      setPharmaModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron guardar los cambios");
    } finally {
      setPharmaSaving(false);
    }
  };

  const [showReviews, setShowReviews] = useState(false);
  const [pendingReviews, setPendingReviews] = useState(0);
  const [reviewBusy, setReviewBusy] = useState(null);
  const [sortField, setSortField] = useState(null);
  // Nota interna del administrador por cliente
  const [noteModal, setNoteModal] = useState(null); // {code, pharmacy, text, updated_at}
  const [paidModal, setPaidModal] = useState(null); // {code, pharmacy, date}
  const [paidSaving, setPaidSaving] = useState(false);

  const savePaidDate = async (clear = false) => {
    if (!paidModal) return;
    setPaidSaving(true);
    try {
      const { data } = await api.patch(`/admin/licenses/${paidModal.code}`, { paid_at: clear ? "" : paidModal.date });
      if (data && data.code) {
        setItems((prev) => prev.map((x) => (x.code === data.code ? { ...x, ...data } : x)));
      }
      toast.success(clear ? "Fecha de cobro borrada" : "Fecha de cobro guardada");
      setPaidModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar la fecha");
    } finally {
      setPaidSaving(false);
    }
  };
  const [noteSaving, setNoteSaving] = useState(false);

  const saveNote = async () => {
    if (!noteModal) return;
    setNoteSaving(true);
    try {
      const { data } = await api.patch(`/admin/licenses/${noteModal.code}`, { notes: noteModal.text || "" });
      if (data && data.code) {
        setItems((prev) => prev.map((x) => (x.code === data.code ? { ...x, ...data } : x)));
      }
      toast.success("Nota guardada");
      setNoteModal(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar la nota");
    } finally {
      setNoteSaving(false);
    }
  };
  const [sortDir, setSortDir] = useState("desc");
  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const filtered = useMemo(() => {
    let list = items.filter((it) => {
      const active = LICENSE_TABS.find((t) => t.id === tab);
      if (active && !active.match(it)) return false;
      if (brandFilter !== "all") {
        const b = it.brand || "farmaclass";
        if (b !== brandFilter) return false;
      }
      if (!filter.trim()) return true;
      const q = filter.toLowerCase();
      return (
        it.code.toLowerCase().includes(q) ||
        (it.user?.pharmacy_name || "").toLowerCase().includes(q) ||
        (it.user?.email || "").toLowerCase().includes(q) ||
        (it.user?.province || "").toLowerCase().includes(q) ||
        (it.credentials_sent_to || "").toLowerCase().includes(q)
      );
    });
    if (sortField) {
      const readValue = (it) => {
        if (sortField === "last_login_at") return it.last_login_at || it.user?.last_login_at || "";
        if (sortField === "code") return it.code || "";
        if (sortField === "pharmacy") return it.user?.pharmacy_name || "";
        if (sortField === "province") return it.user?.province || "";
        if (sortField === "last_disp") return it.dispensation_summary?.last_created_at || "";
        if (sortField === "created_at") return it.created_at || "";
        if (sortField === "renews_at") return it.renews_at || "";
        if (sortField === "first_login_at") return it.first_login_at || it.activated_at || "";
        if (sortField === "status") {
          const rank = { active: 0, activated: 1, available: 2, cancelled: 3 };
          return String(rank[it.status] ?? 9);
        }
        if (sortField === "billing") {
          // Cobradas primero, luego facturadas pendientes de cobro, luego sin facturar.
          const rank = it.paid ? 0 : it.billed ? 1 : 2;
          const amount = Math.round(Number(it.billed_amount || it.amount || 0));
          return `${rank}-${String(999999 - amount).padStart(6, "0")}`;
        }
        if (sortField === "billable") {
          return String(it.billing_consent ? 0 : (it.license_type || "").startsWith("subscription") ? 1 : 2);
        }
        return "";
      };
      list = [...list].sort((a, b) => {
        const va = readValue(a);
        const vb = readValue(b);
        // Empujar strings vacías al final independientemente de la dirección
        if (!va && vb) return 1;
        if (va && !vb) return -1;
        if (!va && !vb) return 0;
        const cmp = String(va).localeCompare(String(vb));
        return sortDir === "asc" ? cmp : -cmp;
      });
    }
    return list;
  }, [items, filter, tab, brandFilter, sortField, sortDir]);

  const stats = useMemo(() => ({
    total: items.length,
    available: items.filter((x) => x.status === "available").length,
    activated: items.filter((x) => x.status === "activated").length,
    active: items.filter((x) => x.status === "active").length,
    cancelled: items.filter((x) => x.status === "cancelled").length,
    sub_monthly: items.filter((x) => x.license_type === "subscription_monthly").length,
    sub_annual: items.filter((x) => x.license_type === "subscription_annual").length,
    single: items.filter((x) => x.license_type === "single_use").length,
    consents: items.filter((x) => x.billing_consent).length,
    billed: items.filter((x) => x.billed).length,
    unbilled: items.filter((x) => !x.billed && x.status !== "available" && x.status !== "cancelled").length,
  }), [items]);

  // Caja del año: facturado por fecha de factura y cobrado por fecha de cobro.
  const cash = useMemo(() => {
    const y = new Date().getFullYear();
    const inYear = (d) => !!d && new Date(d).getFullYear() === y;
    let billed = 0, collected = 0, pending = 0, nBilled = 0, nCollected = 0, nPending = 0, nEstimated = 0;
    for (const l of items) {
      const amount = licAmount(l);
      if (l.billed && inYear(l.billed_at)) {
        billed += amount;
        nBilled += 1;
        if (licAmountIsEstimated(l)) nEstimated += 1;
      }
      if (l.paid && inYear(l.paid_at)) { collected += amount; nCollected += 1; }
      if (l.billed && !l.paid) { pending += amount; nPending += 1; }
    }
    return { year: y, billed, collected, pending, nBilled, nCollected, nPending, nEstimated };
  }, [items]);

  const tabStats = useMemo(() => {
    const out = {};
    for (const t of LICENSE_TABS) {
      const list = items.filter(t.match);
      out[t.id] = { count: list.length, amount: list.reduce((a, l) => a + licAmount(l), 0) };
    }
    return out;
  }, [items]);

  const toggleBilled = async (code, next) => {
    // Optimista: actualiza UI antes de la llamada
    setItems((prev) => prev.map((x) => x.code === code ? { ...x, billed: next, billed_at: next ? new Date().toISOString() : null } : x));
    try {
      await api.patch(`/admin/licenses/${code}`, { billed: next });
      toast.success(next ? `Marcada como facturada · ${code}` : `Facturación revertida · ${code}`);
    } catch (err) {
      // rollback
      setItems((prev) => prev.map((x) => x.code === code ? { ...x, billed: !next } : x));
      toast.error(err?.response?.data?.detail || "No se pudo actualizar");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setCreating(true);
    const sendTo = createSendEmail.trim();
    if (sendTo && !sendTo.includes("@")) {
      toast.error("Email destino no válido");
      setCreating(false);
      return;
    }
    try {
      const payload = { count: Number(count), prefix };
      if (sendTo) payload.send_to_email = sendTo;
      if (createPharmacy.trim()) payload.pharmacy_name = createPharmacy.trim();
      if (createBrand) payload.brand = createBrand;
      const { data } = await api.post("/admin/licenses/bulk", payload);
      setCreatedResult(data.licenses);
      if (data.emails_sent > 0) {
        toast.success(`${data.created} licencia(s) creadas · ${data.emails_sent} email(s) enviado(s)`);
      } else {
        toast.success(`${data.created} licencia(s) creadas`);
      }
      if (data.emails_failed > 0) {
        toast.error(`${data.emails_failed} email(s) no se pudieron enviar`);
      }
      setCreateSendEmail("");
      setCreatePharmacy("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear licencias");
    } finally {
      setCreating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Copiado");
  };

  const openRenewalOffer = async (lic) => {
    const uid = lic.user_id || lic.user?.id;
    if (!uid) {
      toast.error("Esta licencia todavía no está activada por un cliente");
      return;
    }
    const email = lic.user?.email || lic.credentials_sent_to || "";
    const pharmacy = lic.user?.pharmacy_name || lic.pharmacy_name || "";
    setRenewalModal({ user_id: uid, pharmacy_name: pharmacy, email, license_code: lic.code });
    setRenewalPreview(null);
    setRenewalStats(null);
    setRenewalLoading(true);
    try {
      const [{ data: preview }, statsResult] = await Promise.all([
        api.post(`/admin/users/${uid}/send-renewal-offer`, { dry_run: true }),
        api.get(`/admin/users/${uid}/renewal-stats`).catch(() => ({ data: null })),
      ]);
      setRenewalPreview(preview);
      setRenewalStats(statsResult.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando preview");
    } finally {
      setRenewalLoading(false);
    }
  };

  const sendRenewalOffer = async () => {
    if (!renewalModal?.user_id) return;
    setRenewalSending(true);
    try {
      const { data } = await api.post(`/admin/users/${renewalModal.user_id}/send-renewal-offer`, { dry_run: false });
      toast.success(`Oferta enviada a ${data.recipient}`);
      setRenewalModal(null);
      setRenewalPreview(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al enviar");
    } finally {
      setRenewalSending(false);
    }
  };

  const cancelLicense = async (code) => {
    if (!window.confirm(`¿Cancelar la licencia ${code}?`)) return;
    try {
      await api.patch(`/admin/licenses/${code}`, { status: "cancelled" });
      toast.success("Cancelada");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const deleteLicense = async (code, force = false) => {
    const baseMsg = force
      ? `⚠️ Borrar DEFINITIVAMENTE la licencia ${code}.\n\nLa licencia ha sido USADA por un usuario. Al borrarla:\n  · El usuario quedará sin licencia activa (estado "cancelled")\n  · El usuario y sus datos NO se borran (gestiónalo desde /admin/users)\n\n¿Continuar?`
      : `¿Borrar la licencia ${code}?`;
    if (!window.confirm(baseMsg)) return;
    try {
      await api.delete(`/admin/licenses/${code}${force ? "?force=true" : ""}`);
      toast.success("Licencia borrada");
      load();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409 && !force) {
        return deleteLicense(code, true);
      }
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-[1700px]" data-testid="licenses-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">Administración</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter text-slate-950">Licencias</h1>
          <p className="text-slate-500 mt-2 max-w-2xl text-sm">
            Crea licencias, regenera contraseñas, corrige emails y consulta el estado de dispensación de cada farmacia.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={openReleaseModal}
            data-testid="open-release-notes"
            className="inline-flex items-center gap-2 bg-white border border-sky-200 text-sky-800 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-sky-50"
          >
            <Megaphone className="h-4 w-4" />
            Informe de mejoras
          </button>
          <button
            onClick={() => downloadXlsx("/admin/licenses/export", "farmaclass_licencias.xlsx")}
            data-testid="export-licenses"
            className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-900 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-slate-50"
          >
            <Download className="h-4 w-4" />
            Exportar Excel
          </button>
          <button
            onClick={() => setShowReviews(true)}
            data-testid="open-reviews-admin"
            className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-900 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-slate-50"
          >
            <Star className="h-4 w-4 text-amber-500" />
            Reseñas
            {pendingReviews > 0 && (
              <span className="bg-amber-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5">
                {pendingReviews}
              </span>
            )}
          </button>
          <button
            onClick={() => { setCreatedResult(null); setShowCreate(!showCreate); }}
            data-testid="open-create-licenses"
            className="inline-flex items-center gap-2 bg-slate-950 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-slate-800"
          >
            <Plus className="h-4 w-4" />
            Crear licencias
          </button>
        </div>
      </div>

      {showReviews && (
        <ReviewsAdminModal onClose={() => setShowReviews(false)} onChanged={loadPendingReviews} />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3" data-testid="lic-cash-summary">
        <CashStat
          label={`Facturado ${cash.year}`}
          value={cash.billed}
          sub={`${cash.nBilled} ${cash.nBilled === 1 ? "factura" : "facturas"}${
            cash.nEstimated ? ` · ${cash.nEstimated} estimada${cash.nEstimated === 1 ? "" : "s"} por tarifa` : ""
          }`}
          cls="text-slate-900"
          testid="cash-billed"
        />
        <CashStat
          label={`Cobrado ${cash.year}`}
          value={cash.collected}
          sub={`${cash.nCollected} ${cash.nCollected === 1 ? "licencia cobrada" : "licencias cobradas"}`}
          cls="text-emerald-700"
          testid="cash-collected"
        />
        <CashStat
          label="Pendiente de cobro"
          value={cash.pending}
          sub={`${cash.nPending} ${cash.nPending === 1 ? "factura sin cobrar" : "facturas sin cobrar"}`}
          cls={cash.pending > 0 ? "text-amber-700" : "text-slate-400"}
          testid="cash-pending"
          onClick={() => setTab("billed_unpaid")}
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Stat label="Total" value={stats.total} />
        <Stat label="Disponibles" value={stats.available} cls="text-emerald-700" />
        <Stat label="En uso" value={stats.active + stats.activated} cls="text-blue-700" />
        <Stat label="Suscripciones activas" value={stats.sub_monthly + stats.sub_annual} cls="text-slate-900" />
      </div>

      {duplicates && duplicates.total_groups > 0 && (
        <div
          className="bg-rose-50 border-2 border-rose-300 rounded-lg p-4 mb-6 flex flex-wrap items-center justify-between gap-3"
          data-testid="duplicate-studies-banner"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-rose-900 text-sm">
                Estudios duplicados detectados · {duplicates.total_groups} {duplicates.total_groups === 1 ? "farmacia" : "farmacias"} con periodos repetidos
              </div>
              <div className="text-xs text-rose-800 mt-0.5">
                Al haber varias dispensaciones en el mismo mes, la evolución de personal puede aparecer incompleta. Revísalos y consolida.
              </div>
            </div>
          </div>
          <button
            onClick={() => setShowDuplicatesModal(true)}
            className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white px-4 py-2 rounded-md text-sm font-semibold"
            data-testid="open-duplicates-modal"
          >
            <Users className="h-4 w-4" />
            Revisar {duplicates.total_groups} caso{duplicates.total_groups === 1 ? "" : "s"}
          </button>
        </div>
      )}

      {showCreate && (
        <div className="bg-amber-50/40 border-2 border-amber-200 rounded-lg p-5 mb-6" data-testid="create-licenses-form">
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <label className="block">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Cantidad</div>
              <input data-testid="lic-count" type="number" min="1" max="500" value={count} onChange={(e) => setCount(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-md font-mono-num"/>
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Prefijo (1-8 letras/números)</div>
              <input data-testid="lic-prefix" type="text" maxLength={8} value={prefix} onChange={(e) => setPrefix(e.target.value.toUpperCase())} className="w-full px-3 py-2 border border-slate-200 rounded-md font-mono-num"/>
            </label>
            <div className="md:col-span-3 border-t border-slate-200 pt-3 mt-1">
              <div className="text-[10px] uppercase tracking-wider text-sky-700 font-bold mb-2 inline-flex items-center gap-1.5">
                <Mail className="h-3 w-3" /> Envío automático (opcional)
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Email de la farmacia</div>
                  <input data-testid="lic-send-email" type="email" placeholder="farmacia@ejemplo.com" value={createSendEmail} onChange={(e) => setCreateSendEmail(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-md"/>
                  <div className="text-[10px] text-slate-500 mt-1">Si rellenas este campo, las credenciales se envían tras crear.</div>
                </label>
                <label className="block">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Nombre farmacia (opcional)</div>
                  <input data-testid="lic-pharmacy-hint" type="text" placeholder="Farmacia Sánchez" value={createPharmacy} onChange={(e) => setCreatePharmacy(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-md"/>
                  <div className="text-[10px] text-slate-500 mt-1">Se anota como hint hasta que el cliente complete su onboarding.</div>
                </label>
                <label className="block">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Tipo de licencia</div>
                  <select
                    data-testid="lic-brand"
                    value={createBrand}
                    onChange={(e) => setCreateBrand(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md bg-white"
                  >
                    <option value="">FarmaClass (completa)</option>
                    <option value="sandoz">Sandoz (sin condiciones de laboratorio)</option>
                    <option value="normon">Normon (FarmaClass completo + botones Normon)</option>
                    <option value="aurovitas">Aurovitas (FarmaClass completo + botones Aurovitas)</option>
                  </select>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Sandoz: logo y colores propios, sin Condiciones ni Comparativa, con sus pedidos directos.
                    Normon y Aurovitas: FarmaClass completo con su logo, colores y los tres botones de pedido
                    a su laboratorio.
                  </p>
                </label>
              </div>
            </div>
            <button type="submit" disabled={creating} data-testid="lic-create-submit" className="md:col-span-3 bg-slate-950 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-slate-800 disabled:opacity-50">
              {creating ? "Creando…" : createSendEmail.trim() ? `Crear ${count} licencia(s) y enviar email` : `Crear ${count} licencia(s)`}
            </button>
          </form>

          {createdResult && (
            <div className="mt-4 bg-white border border-emerald-200 rounded-md p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold text-emerald-700 text-sm">✓ {createdResult.length} licencias creadas.</div>
                <button onClick={() => copyToClipboard(createdResult.map((l) => `${l.code}\t${l.password}`).join("\n"))} className="text-xs px-3 py-1.5 border border-slate-200 rounded-md hover:bg-slate-50 inline-flex items-center gap-1.5">
                  <Copy className="h-3 w-3" /> Copiar todas
                </button>
              </div>
              <div className="max-h-60 overflow-y-auto bg-slate-50 rounded p-3 font-mono-num text-xs space-y-0.5">
                {createdResult.map((l) => (
                  <div key={l.code} className="flex justify-between">
                    <span className="font-bold">{l.code}</span>
                    <span className="text-slate-600">{l.password}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="flex flex-wrap items-center gap-1.5 px-4 pt-4" data-testid="lic-brand-filters">
          <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mr-1">Marca</span>
          {BRAND_FILTERS.map((b) => {
            const count =
              b.id === "all" ? items.length : items.filter((x) => (x.brand || "farmaclass") === b.id).length;
            const on = brandFilter === b.id;
            return (
              <button
                key={b.id}
                onClick={() => setBrandFilter(b.id)}
                data-testid={`lic-brand-filter-${b.id}`}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                  on ? "text-white border-transparent" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                }`}
                style={on ? { backgroundColor: b.color } : undefined}
              >
                {b.label}
                <span className={`ml-2 font-mono-num tabular-nums ${on ? "text-white/70" : "text-slate-400"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-1.5 px-4 pt-3" data-testid="lic-tabs">
          {LICENSE_TABS.map((t) => {
            const st = tabStats[t.id] || { count: 0, amount: 0 };
            const on = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`lic-tab-${t.id}`}
                className={`text-xs font-semibold px-3.5 py-2 rounded-lg border transition-colors ${
                  on
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-400 hover:text-slate-900"
                }`}
              >
                {t.label}
                <span className={`ml-2 font-mono-num tabular-nums ${on ? "text-emerald-300" : "text-slate-400"}`}>
                  {st.count}
                </span>
                {t.amount && st.amount > 0 && (
                  <span className={`ml-1.5 font-mono-num tabular-nums ${on ? "text-white/70" : "text-slate-400"}`}>
                    · {Math.round(st.amount).toLocaleString("es-ES")} €
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="p-4 border-b border-slate-200 flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[200px] flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-md">
            <Search className="h-4 w-4 text-slate-400" />
            <input data-testid="lic-search" type="text" placeholder="Buscar por código, farmacia, email, provincia…" value={filter} onChange={(e) => setFilter(e.target.value)} className="w-full bg-transparent outline-none text-sm"/>
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none" title="Muestra licencias creadas por la suite de pytest (emails @test.local, @example.com, TEST_*)">
            <input type="checkbox" checked={includeTest} onChange={(e) => setIncludeTest(e.target.checked)} data-testid="lic-toggle-test" className="rounded"/>
            Mostrar licencias de test
          </label>
        </div>

        <div className="overflow-x-auto" ref={tableScrollRef}>
          <table className="w-full text-sm" data-testid="licenses-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-2 py-2 cursor-pointer select-none hover:text-slate-900" onClick={() => toggleSort("code")} data-testid="sort-code">
                  Código {sortField === "code" && (sortDir === "asc" ? "↑" : "↓")}
                </th>
                <th className="text-left px-2 py-2 cursor-pointer select-none hover:text-slate-900" onClick={() => toggleSort("status")} data-testid="sort-status" title="Ordenar por estado: en uso, activadas, disponibles, canceladas">
                  Estado {sortField === "status" && (sortDir === "asc" ? "↑" : "↓")}
                </th>
                <th className="text-left px-2 py-2 cursor-pointer select-none hover:text-slate-900" onClick={() => toggleSort("pharmacy")} data-testid="sort-pharmacy">
                  Farmacia · Email {sortField === "pharmacy" && (sortDir === "asc" ? "↑" : "↓")}
                </th>
                <th className="text-center px-2 py-2 cursor-pointer select-none hover:text-slate-900" onClick={() => toggleSort("billing")} data-testid="sort-billing" title="Ordenar por facturación: cobradas, facturadas pendientes de cobro y sin facturar">
                  Facturada {sortField === "billing" && (sortDir === "asc" ? "↑" : "↓")}
                  <div className="normal-case tracking-normal text-[9px] text-slate-400 hover:text-slate-900" onClick={(e) => { e.stopPropagation(); toggleSort("billable"); }} data-testid="sort-billable" title="Ordenar por consentimiento de facturación">
                    facturable {sortField === "billable" && (sortDir === "asc" ? "↑" : "↓")}
                  </div>
                </th>
                <th className="text-left px-2 py-2 cursor-pointer select-none hover:text-slate-900" onClick={() => toggleSort("renews_at")} data-testid="sort-renewal" title="Fecha de cobro y renovación (1 año de licencia)">
                  Cobro / Renov. {sortField === "renews_at" && (sortDir === "asc" ? "↑" : "↓")}
                </th>
                <th className="text-left px-2 py-2 cursor-pointer select-none hover:text-slate-900" onClick={() => toggleSort("province")} data-testid="sort-province">
                  Provincia {sortField === "province" && (sortDir === "asc" ? "↑" : "↓")}
                </th>
                <th className="text-left px-2 py-2 select-none" title="Alta de la licencia, primer acceso y última entrada del cliente">
                  <span className="cursor-pointer hover:text-slate-900" onClick={() => toggleSort("created_at")} data-testid="sort-created-at">Alta{sortField === "created_at" && (sortDir === "asc" ? "↑" : "↓")}</span>
                  {" · "}
                  <span className="cursor-pointer hover:text-slate-900" onClick={() => toggleSort("first_login_at")} data-testid="sort-first-login">1.º acceso{sortField === "first_login_at" && (sortDir === "asc" ? "↑" : "↓")}</span>
                  {" · "}
                  <span className="cursor-pointer hover:text-slate-900" onClick={() => toggleSort("last_login_at")} data-testid="sort-last-login">Última{sortField === "last_login_at" && (sortDir === "asc" ? "↑" : "↓")}</span>
                </th>
                <th className="text-left px-2 py-2 cursor-pointer select-none hover:text-slate-900" onClick={() => toggleSort("last_disp")} data-testid="sort-last-disp">
                  Última dispensación {sortField === "last_disp" && (sortDir === "asc" ? "↑" : "↓")}
                </th>
                <th className="text-right px-2 py-2 sticky right-0 z-20 bg-slate-50 shadow-[-6px_0_8px_-6px_rgba(15,23,42,0.18)]">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && <tr><td colSpan={9} className="p-8 text-center text-slate-400">Cargando…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={9} className="p-8 text-center text-slate-400">Sin licencias</td></tr>
              )}
              {filtered.map((l) => {
                const st = STATUS_LABELS[l.status] || { label: l.status, cls: "bg-slate-100 text-slate-700" };
                const s = l.dispensation_summary;
                const currentEmail = l.user?.email || l.credentials_sent_to || "";
                const isExpanded = expandedCode === l.code;
                const isAvailable = l.status === "available" && !l.user_id;
                const isPwVisible = showPwFor === l.code;
                return (
                  <Fragment key={l.code}>
                  <tr className={`hover:bg-slate-50 ${l.is_test ? "bg-amber-50/30" : ""} ${isExpanded ? "border-b-0" : ""}`}>
                    <td className="px-2 py-2 font-mono-num font-bold whitespace-nowrap">
                      <div className="inline-flex items-center gap-1.5">
                        <button
                          onClick={() => toggleExpand(l.code)}
                          className="text-slate-400 hover:text-slate-900"
                          title={isExpanded ? "Ocultar resumen" : "Ver resumen consultoría"}
                          data-testid={`lic-expand-${l.code}`}
                        >
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                        {l.code}
                        {l.is_test && <span className="ml-1 inline-block px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-[9px] font-bold uppercase" title="Licencia de test">TEST</span>}
                        {l.brand && <span className="ml-1 inline-block px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded text-[9px] font-bold uppercase" title={`Licencia de marca blanca ${l.brand}`} data-testid={`lic-brand-badge-${l.code}`}>{l.brand}</span>}
                      </div>
                      {isAvailable && l.plain_initial_password && (
                        <div className="mt-1.5 flex items-center gap-1 text-[11px]" data-testid={`lic-plain-pw-${l.code}`}>
                          <span className="text-slate-500 font-normal">🔓</span>
                          {isPwVisible ? (
                            <span className="font-mono-num text-slate-700 select-all">{l.plain_initial_password}</span>
                          ) : (
                            <span className="font-mono-num text-slate-400">••••••••</span>
                          )}
                          <button onClick={() => setShowPwFor(isPwVisible ? null : l.code)} className="p-0.5 rounded text-slate-400 hover:text-slate-700" title={isPwVisible ? "Ocultar" : "Mostrar"}>
                            {isPwVisible ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                          </button>
                          <button onClick={() => copyToClipboard(l.plain_initial_password)} className="p-0.5 rounded text-slate-400 hover:text-slate-700" title="Copiar">
                            <Copy className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2"><span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold ${st.cls}`}>{st.label}</span></td>
                    <td className="px-2 py-2">
                      <div className="font-medium text-slate-900">{l.user?.pharmacy_name || <span className="text-slate-400 italic">Sin activar</span>}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                        <span className="truncate max-w-[190px]" title={currentEmail}>{currentEmail || "—"}</span>
                        {(l.status !== "cancelled") && (
                          <button
                            onClick={() => { setEmailModal({ code: l.code, currentEmail }); setEmailInput(currentEmail); }}
                            className="text-slate-400 hover:text-sky-600"
                            title="Editar email del cliente"
                            data-testid={`lic-edit-email-${l.code}`}
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                      <button
                        onClick={() => setNoteModal({ code: l.code, pharmacy: l.user?.pharmacy_name || l.pharmacy_name_hint || "", text: l.notes || "", updated_at: l.notes_updated_at })}
                        className={`mt-1.5 inline-flex items-start gap-1 max-w-[200px] text-left px-2 py-1 rounded-md text-[11px] font-semibold border transition-colors ${
                          l.notes
                            ? "bg-amber-50 border-amber-300 text-amber-900 hover:bg-amber-100"
                            : "bg-white border-dashed border-slate-300 text-slate-400 hover:text-slate-700 hover:border-slate-500"
                        }`}
                        title={l.notes ? l.notes : "Añadir nota interna sobre este cliente"}
                        data-testid={`lic-note-btn-${l.code}`}
                      >
                        <StickyNote className="h-3.5 w-3.5 shrink-0 mt-[1px]" />
                        <span className="truncate">{l.notes ? l.notes : "Añadir nota"}</span>
                      </button>
                    </td>
                    <td className="px-2 py-2 text-center">
                      <BillingCell
                        license={l}
                        onUpdate={async (patchBody) => {
                          // Optimistic update: refleja el cambio inmediatamente
                          // (por si el backend tarda o la respuesta viene delgada).
                          const nowIso = new Date().toISOString();
                          const optimistic = { ...patchBody };
                          if (patchBody.paid === true) optimistic.paid_at = nowIso;
                          if (patchBody.paid === false) optimistic.paid_at = null;
                          if (patchBody.billed === true) optimistic.billed_at = nowIso;
                          if (patchBody.billed === false) optimistic.billed_at = null;
                          setItems((prev) => prev.map((x) => x.code === l.code ? { ...x, ...optimistic } : x));
                          try {
                            const { data } = await api.patch(`/admin/licenses/${l.code}`, patchBody);
                            if (data && data.code) {
                              setItems((prev) => prev.map((x) => x.code === l.code ? { ...x, ...data } : x));
                            }
                            toast.success("Cambios guardados");
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || "Error al guardar");
                            load();
                          }
                        }}
                      />
                      <div className="mt-1" title="Consentimiento de facturación">
                        {l.billing_consent ? (
                          <span className="inline-block px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-[9px] font-bold uppercase">Facturable</span>
                        ) : l.license_type?.startsWith("subscription") ? (
                          <span className="inline-block px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded-full text-[9px] font-bold uppercase">No facturable</span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-2 py-2 text-xs whitespace-nowrap" data-testid={`lic-renewal-${l.code}`}>
                      <RenewalCell
                        license={l}
                        onEdit={() => setPaidModal({
                          code: l.code,
                          pharmacy: l.user?.pharmacy_name || l.pharmacy_name_hint || "",
                          date: (l.paid_at || "").slice(0, 10),
                        })}
                      />
                    </td>
                    <td className="px-2 py-2 text-slate-600 text-xs">
                      <div className="inline-flex items-center gap-1.5">
                        <span title={l.user?.ccaa || ""}>{l.user?.province || "—"}</span>
                        {l.user_id && l.status !== "cancelled" && (
                          <button
                            onClick={() => openPharmaModal(l.code)}
                            className="text-slate-400 hover:text-sky-600"
                            title="Editar datos de la farmacia (nombre, teléfono, provincia)"
                            data-testid={`lic-edit-pharma-${l.code}`}
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-2 text-[11px] whitespace-nowrap leading-tight">
                      <div className="text-slate-500" data-testid={`lic-created-at-${l.code}`}>Alta {formatDate(l.created_at)}</div>
                      <div data-testid={`lic-first-login-${l.code}`}>
                        {(l.first_login_at || l.activated_at)
                          ? <span className="text-slate-500">1.º {formatDate(l.first_login_at || l.activated_at)}</span>
                          : <span className="text-slate-400 italic">Sin acceso</span>}
                      </div>
                      <div data-testid={`lic-last-login-${l.code}`}>
                        {(l.last_login_at || l.user?.last_login_at) ? (
                          <>
                            <div className="text-slate-800 font-medium">Últ. {formatDateTime(l.last_login_at || l.user?.last_login_at)}</div>
                            <InactivityBadge days={daysSince(l.last_login_at || l.user?.last_login_at)} code={l.code} />
                          </>
                        ) : (
                          <span className="text-slate-400 italic">Nunca entró</span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-2 text-xs">
                      {s ? (
                        <div>
                          <div className="font-medium text-slate-900">{s.last_label || "—"}</div>
                          {(s.last_period_start || s.last_period_end) && (
                            <div className="text-[11px] text-slate-700 font-mono-num" title="Período (mes) que cubre el último estudio">
                              📅 {formatPeriod(s.last_period_start, s.last_period_end)}
                            </div>
                          )}
                          <div className="text-[10px] text-slate-400" title="Fecha de subida">
                            subido {formatDate(s.last_created_at)}
                          </div>
                          <div className="text-[10px] text-slate-500 font-mono-num" title="Meses de histórico · líneas del último estudio">
                            {s.months_count ?? "—"} meses · {s.last_total_lines != null ? s.last_total_lines.toLocaleString("es-ES") : "—"} líneas
                          </div>
                        </div>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-2 py-2 text-right sticky right-0 z-10 bg-white shadow-[-6px_0_8px_-6px_rgba(15,23,42,0.18)]">
                      <div className="inline-flex items-center gap-2">
                        {l.status !== "cancelled" && (l.user_id || l.credentials_sent_to) && (
                          <button
                            onClick={() => {
                              setMsgModal({
                                code: l.code,
                                pharmacy: l.user?.pharmacy_name || l.pharmacy_name_hint || "",
                                email: l.user?.email || l.activated_user_email || l.credentials_sent_to || "",
                                last_sent_at: l.last_consultancy_email_sent_at || null,
                              });
                              setMsgRecipient(l.user?.email || l.activated_user_email || l.credentials_sent_to || "");
                              setMsgSubject("");
                              setMsgBody("");
                              setMsgAttachPdf(false);
                            }}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-700 hover:text-indigo-900 bg-indigo-50 border border-indigo-200 p-1 rounded-md"
                            title="Escribir un email al cliente (opcionalmente con el informe de consultoría adjunto)"
                            data-testid={`lic-message-${l.code}`}
                          >
                            <MessageSquare className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {l.status !== "cancelled" && (
                          <button
                            onClick={() => askReview(l.code)}
                            disabled={reviewBusy === l.code}
                            className="text-amber-600 hover:text-amber-800 disabled:opacity-50"
                            title={l.last_review_request_at
                              ? `Pedir reseña otra vez (última petición: ${formatDate(l.last_review_request_at)})`
                              : "Enviar al cliente el enlace para dejar una reseña"}
                            data-testid={`lic-review-${l.code}`}
                          >
                            <Star className={`h-4 w-4 ${l.last_review_request_at ? "fill-amber-400" : ""}`} />
                          </button>
                        )}
                        {l.status !== "cancelled" && (
                          <>
                            <button
                              onClick={() => { setRegenModal({ code: l.code }); setShowPw(false); }}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-purple-700 hover:text-purple-900"
                              title="Regenerar contraseña y verla ahora"
                              data-testid={`lic-regen-${l.code}`}
                            >
                              <Key className="h-3.5 w-3.5" />
                              
                            </button>
                            <button
                              onClick={() => {
                                const autoEmail = l.credentials_sent_to || l.activated_user_email;
                                if (autoEmail) sendCredentialsAuto(l);
                                else { setSendModal({ code: l.code }); setSendEmail(""); }
                              }}
                              disabled={sendingCode === l.code}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-sky-700 hover:text-sky-900 disabled:opacity-50"
                              title={(l.credentials_sent_to || l.activated_user_email) ? `Regenerar y enviar por email` : "Regenerar y enviar por email al cliente"}
                              data-testid={`lic-send-${l.code}`}
                            >
                              <Mail className="h-3.5 w-3.5" />
                              {sendingCode === l.code && <Loader2 className="h-3 w-3 animate-spin" />}
                            </button>
                          </>
                        )}
                        {l.status !== "cancelled" && l.user_id && (
                          <button
                            onClick={() => setReminderModal({
                              code: l.code,
                              user_id: l.user_id,
                              email: l.user?.email || l.activated_user_email || "",
                              pharmacy: l.user?.pharmacy_name || l.pharmacy_name_hint || "",
                              days: daysSince(l.last_login_at || l.user?.last_login_at),
                              last_reminder_at: l.user?.last_reminder_sent_at || null,
                              reminders_count: l.user?.reminders_sent_count || 0,
                            })}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-900"
                            title="Enviar email de recordatorio por días sin entrar"
                            data-testid={`lic-reminder-${l.code}`}
                          >
                            <BellRing className="h-3.5 w-3.5" />
                            
                          </button>
                        )}
                        {l.status !== "cancelled" && l.user_id && (
                          <button
                            onClick={() => openRenewalOffer(l)}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900"
                            title="Enviar email con oferta de renovación (300 € + IVA vs 500 €/año)"
                            data-testid={`lic-renewal-${l.code}`}
                          >
                            <Handshake className="h-3.5 w-3.5" />
                            
                          </button>
                        )}
                        {l.status !== "available" && l.status !== "cancelled" && (
                          <button onClick={() => cancelLicense(l.code)} className="text-xs text-amber-600 hover:text-amber-800 font-medium" title="Cancelar (mantiene el registro)" data-testid={`lic-cancel-${l.code}`}><Ban className="h-4 w-4" /></button>
                        )}
                        <button onClick={() => deleteLicense(l.code)} className="text-slate-400 hover:text-rose-600" title={l.user_id ? "Borrar (incluso si está activada)" : "Borrar"} data-testid={`lic-delete-${l.code}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr key={l.code + "-detail"} className="bg-slate-50/60">
                      <td colSpan={9} className="p-0">
                        {/* La tabla es más ancha que la pantalla: el detalle se
                            fija al borde visible y se limita al ancho útil para
                            que no se corte por la derecha. */}
                        <div
                          className="sticky left-0 mx-auto"
                          style={detailWidth ? { width: detailWidth, maxWidth: "100%" } : undefined}
                        >
                          <ConsultancySummary
                            state={summaryCache[l.code]}
                            onGeneratePdf={() => { setPdfModal({ code: l.code, pharmacy_name: l.user?.pharmacy_name, email: l.user?.email || l.credentials_sent_to || "" }); setPdfNotes(""); setPdfRecs(""); setPdfNextVisit(""); setPdfSendEmail(false); setPdfRecipient(l.user?.email || l.credentials_sent_to || ""); setPdfCustomMessage(""); setPdfCustomSubject(""); }}
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal: email libre del consultor al cliente */}
      {msgModal && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => !msgSending && setMsgModal(null)}
          data-testid="client-message-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-200">
              <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center">
                <MessageSquare className="h-4 w-4 text-indigo-700" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">Email al cliente</h2>
                <p className="text-xs text-slate-500">{msgModal.pharmacy || msgModal.code}</p>
              </div>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Destinatario</label>
                <input
                  type="email"
                  value={msgRecipient}
                  onChange={(e) => setMsgRecipient(e.target.value)}
                  placeholder="farmacia@ejemplo.com"
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                  data-testid="msg-recipient"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Asunto (opcional)</label>
                <input
                  type="text"
                  value={msgSubject}
                  onChange={(e) => setMsgSubject(e.target.value)}
                  placeholder={`FarmaClass · ${msgModal.pharmacy || msgModal.code}`}
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                  data-testid="msg-subject"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Mensaje</label>
                <textarea
                  value={msgBody}
                  onChange={(e) => setMsgBody(e.target.value)}
                  rows={7}
                  placeholder="Ej: Hola Pedro, repasando tu último estudio he visto que ATORVASTATINA sigue en marca…"
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                  data-testid="msg-body"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={msgAttachPdf}
                  onChange={(e) => setMsgAttachPdf(e.target.checked)}
                  data-testid="msg-attach-pdf"
                />
                Adjuntar el <strong>informe de consultoría (PDF)</strong>
              </label>
              {msgModal.last_sent_at && (
                <div className="text-xs text-slate-500">
                  Último email enviado: {formatDateTime(msgModal.last_sent_at)}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200">
              <button
                onClick={() => setMsgModal(null)}
                disabled={msgSending}
                className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900"
                data-testid="msg-cancel"
              >
                Cancelar
              </button>
              <button
                onClick={sendClientMessage}
                disabled={msgSending || !msgRecipient.trim() || !msgBody.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-bold rounded-lg"
                data-testid="msg-send"
              >
                {msgSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {msgSending ? "Enviando…" : "Enviar email"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: recordatorio por días sin entrar */}
      {reminderModal && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => !reminderSending && setReminderModal(null)}
          data-testid="reminder-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-200">
              <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
                <BellRing className="h-4 w-4 text-amber-700" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">Enviar recordatorio</h2>
                <p className="text-xs text-slate-500">{reminderModal.pharmacy || reminderModal.code}</p>
              </div>
            </div>
            <div className="px-6 py-5 text-sm space-y-3">
              <div className="flex justify-between gap-3">
                <span className="text-slate-500">Destinatario</span>
                <strong className="text-slate-900 text-right break-all" data-testid="reminder-email">{reminderModal.email || "sin email"}</strong>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-slate-500">Días sin entrar</span>
                <strong className={reminderModal.days >= INACTIVITY_THRESHOLD_DAYS ? "text-rose-700" : "text-slate-900"} data-testid="reminder-days">
                  {reminderModal.days == null ? "nunca ha entrado" : `${reminderModal.days} días`}
                </strong>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-slate-500">Último recordatorio</span>
                <strong className="text-slate-900">
                  {reminderModal.last_reminder_at
                    ? `${formatDateTime(reminderModal.last_reminder_at)} (${reminderModal.reminders_count} enviados)`
                    : "ninguno"}
                </strong>
              </div>
              {!reminderModal.email && (
                <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-2.5">
                  Esta licencia no tiene un email real configurado: edítalo antes de enviar.
                </div>
              )}
              <p className="text-xs text-slate-500">
                Se envía el email de "te echamos de menos" con su último acceso y un enlace para volver a entrar.
              </p>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200">
              <button
                onClick={() => setReminderModal(null)}
                disabled={reminderSending}
                className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900"
                data-testid="reminder-cancel"
              >
                Cancelar
              </button>
              <button
                onClick={sendInactivityReminder}
                disabled={reminderSending || !reminderModal.email}
                className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm font-bold rounded-lg"
                data-testid="reminder-send"
              >
                {reminderSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {reminderSending ? "Enviando…" : "Enviar recordatorio"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: oferta de renovación (iter-76) */}      {renewalModal && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 md:p-8 overflow-y-auto"
          onClick={() => !renewalSending && setRenewalModal(null)}
          data-testid="renewal-offer-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Handshake className="h-4 w-4 text-emerald-700" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Oferta de renovación</h2>
                  <p className="text-xs text-slate-500">
                    Envío al cliente con su resumen de uso · CTA WhatsApp + email
                  </p>
                </div>
              </div>
              <button
                onClick={() => !renewalSending && setRenewalModal(null)}
                className="text-slate-400 hover:text-slate-900"
                data-testid="renewal-offer-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid md:grid-cols-5 gap-0 max-h-[75vh] overflow-hidden">
              <div className="md:col-span-2 p-6 border-r border-slate-200 space-y-4 overflow-y-auto max-h-[75vh]">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Destinatario</div>
                  <div className="font-mono-num text-sm text-slate-900 break-all">
                    {renewalPreview?.recipient || renewalModal.email || "—"}
                  </div>
                </div>
                {renewalPreview && (
                  <>
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Farmacia</div>
                      <div className="text-sm text-slate-900">{renewalPreview.pharmacy_name || "—"}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-blue-800 mb-1">Primer acceso</div>
                        <div className="text-sm font-semibold text-blue-900">{formatDate(renewalPreview.first_access)}</div>
                      </div>
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-800 mb-1">Estudios</div>
                        <div className="text-2xl font-bold font-mono-num text-emerald-900">{renewalPreview.studies_count}</div>
                      </div>
                    </div>
                  </>
                )}
                {renewalStats && renewalStats.total_sends > 0 && (
                  <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-violet-800 mb-1.5">
                      Historial de envíos ({renewalStats.total_sends})
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div className="text-center">
                        <div className="text-[10px] text-violet-700 font-semibold uppercase tracking-wider">Aperturas</div>
                        <div className="text-2xl font-bold font-mono-num text-violet-900">{renewalStats.total_opens}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-[10px] text-violet-700 font-semibold uppercase tracking-wider">Último abierto</div>
                        <div className="text-xs font-semibold text-violet-900 mt-1">
                          {renewalStats.last_open_at
                            ? new Date(renewalStats.last_open_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short" }) +
                              " " + new Date(renewalStats.last_open_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })
                            : "—"}
                        </div>
                      </div>
                    </div>
                    <div className="text-[10px] text-violet-700 leading-relaxed border-t border-violet-200 pt-1.5">
                      Último envío: {formatDate(renewalStats.last_send_at)}. Los clientes con
                      aperturas &gt; 0 pero sin respuesta son buenos candidatos para llamada.
                    </div>
                  </div>
                )}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 leading-relaxed">
                  <strong>⚠️ El email es noreply</strong>: el cliente sólo podrá responderte por
                  WhatsApp (609 46 77 88) o email (pedro@archiduquecarlos.com), que quedan como
                  botones grandes en el cuerpo del email.
                </div>
                <button
                  onClick={sendRenewalOffer}
                  disabled={renewalSending || renewalLoading || !renewalPreview}
                  className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 text-white px-4 py-3 rounded-md text-sm font-bold hover:bg-emerald-700 disabled:opacity-50"
                  data-testid="renewal-send"
                >
                  {renewalSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Enviar oferta ahora
                </button>
              </div>

              <div className="md:col-span-3 p-6 bg-slate-100 overflow-y-auto max-h-[75vh]">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Vista previa del email</div>
                {renewalLoading ? (
                  <div className="flex items-center justify-center py-16 text-slate-400">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Cargando…
                  </div>
                ) : renewalPreview?.preview_html ? (
                  <div
                    className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden"
                    data-testid="renewal-preview-html"
                    dangerouslySetInnerHTML={{ __html: renewalPreview.preview_html }}
                  />
                ) : (
                  <div className="text-slate-400 text-sm">Sin preview disponible.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: enviar credenciales por email */}
      {sendModal && (
        <Modal onClose={() => { setSendModal(null); setSendEmail(""); setSendRegenerate(false); }} testid="send-credentials-modal">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-bold mb-1">Enviar credenciales</div>
          <div className="font-display text-2xl font-bold tracking-tight inline-flex items-center gap-2 mb-4">
            <Mail className="h-5 w-5 text-sky-600" />
            {sendModal.code}
          </div>
          <div className={`${sendRegenerate ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-sky-50 border-sky-200 text-sky-900"} border rounded-lg p-3 mb-4 text-xs`}>
            {sendRegenerate
              ? "Se generará una contraseña NUEVA y se enviará al cliente. Su contraseña actual dejará de funcionar."
              : "Se reenviará la contraseña ACTUAL sin regenerarla. Solo funciona si la licencia aún no ha sido activada."}
          </div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Email de la farmacia</label>
          <input type="email" value={sendEmail} onChange={(e) => setSendEmail(e.target.value)} placeholder="farmacia@ejemplo.com" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" data-testid="send-modal-email" autoFocus onKeyDown={(e) => { if (e.key === "Enter") sendCredentials(); }}/>
          <label className="mt-3 flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={sendRegenerate}
              onChange={(e) => setSendRegenerate(e.target.checked)}
              className="h-4 w-4 rounded"
              data-testid="send-modal-regenerate"
            />
            <span className="text-sm text-slate-700">
              <strong>Regenerar contraseña</strong> (obligatorio si la licencia ya está activada)
            </span>
          </label>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => { setSendModal(null); setSendEmail(""); setSendRegenerate(false); }} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="send-modal-cancel">Cancelar</button>
            <button onClick={sendCredentials} disabled={sendingCode === sendModal.code} className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white font-bold px-5 py-2 rounded-full text-sm" data-testid="send-modal-confirm">
              {sendingCode === sendModal.code ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              Enviar email
            </button>
          </div>
        </Modal>
      )}

      {/* Modal: regenerar contraseña y verla */}
      {regenModal && (
        <Modal onClose={() => { setRegenModal(null); setShowPw(false); }} testid="regen-modal">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-bold mb-1">Regenerar contraseña</div>
          <div className="font-display text-2xl font-bold tracking-tight inline-flex items-center gap-2 mb-4">
            <Key className="h-5 w-5 text-purple-600" />
            {regenModal.code}
          </div>
          {!regenModal.result ? (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-xs text-amber-900 space-y-1.5">
                <div className="inline-flex items-center gap-1.5 font-bold"><AlertTriangle className="h-3.5 w-3.5" />Atención</div>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>Se generará una <strong>contraseña nueva</strong>. La anterior dejará de funcionar.</li>
                  <li>Se te mostrará <strong>una única vez</strong> en pantalla. No se guarda en claro.</li>
                  <li>Si la licencia está activada, el cliente deberá cambiarla en su próximo acceso.</li>
                  <li>La acción queda registrada en auditoría (LOPD).</li>
                </ul>
              </div>
              <div className="flex justify-end gap-2 mt-5">
                <button onClick={() => setRegenModal(null)} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="regen-cancel">Cancelar</button>
                <button onClick={regeneratePassword} disabled={regenLoading} className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-bold px-5 py-2 rounded-full text-sm" data-testid="regen-confirm">
                  {regenLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Key className="h-4 w-4" />}
                  Regenerar ahora
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4 text-xs text-emerald-900 inline-flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4" />
                Contraseña regenerada. <strong>Cópiala ahora</strong>, no volverá a mostrarse.
              </div>
              <div className="border border-slate-300 rounded-md p-3 bg-slate-50 mb-4">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Nueva contraseña · {regenModal.result.code}</div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 font-mono-num text-lg font-bold text-slate-900 select-all" data-testid="regen-new-password">
                    {showPw ? regenModal.result.new_password : "•".repeat(regenModal.result.new_password.length)}
                  </div>
                  <button onClick={() => setShowPw(!showPw)} className="p-2 rounded hover:bg-slate-200 text-slate-600" title={showPw ? "Ocultar" : "Mostrar"}>
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                  <button onClick={() => copyToClipboard(regenModal.result.new_password)} className="inline-flex items-center gap-1 px-3 py-2 bg-slate-900 text-white text-xs font-bold rounded hover:bg-slate-700" data-testid="regen-copy">
                    <Copy className="h-3.5 w-3.5" /> Copiar
                  </button>
                </div>
              </div>
              {regenModal.result.must_change_password && (
                <div className="text-[11px] text-slate-500 mb-3">El cliente deberá cambiarla en su próximo acceso.</div>
              )}
              <div className="flex justify-end mt-5">
                <button onClick={() => { setRegenModal(null); setShowPw(false); }} className="px-5 py-2 rounded-full bg-slate-900 text-white text-sm font-bold hover:bg-slate-700" data-testid="regen-close">
                  Hecho, cerrar
                </button>
              </div>
            </>
          )}
        </Modal>
      )}

      {/* Modal: editar email cliente */}
      {emailModal && (
        <Modal onClose={() => { setEmailModal(null); setEmailInput(""); }} testid="edit-email-modal">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-bold mb-1">Editar email del cliente</div>
          <div className="font-display text-2xl font-bold tracking-tight inline-flex items-center gap-2 mb-4">
            <Pencil className="h-5 w-5 text-sky-600" />
            {emailModal.code}
          </div>
          <div className="text-xs text-slate-500 mb-1">Email actual</div>
          <div className="font-mono-num text-sm text-slate-700 mb-3">{emailModal.currentEmail || "—"}</div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Nuevo email</label>
          <input type="email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)} placeholder="nuevo@ejemplo.com" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" data-testid="edit-email-input" autoFocus onKeyDown={(e) => { if (e.key === "Enter") saveClientEmail(); }}/>
          <div className="text-[11px] text-slate-500 mt-2">
            Este email se usa como <strong>login</strong> del cliente (si está activado) y como destino de las credenciales.
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => { setEmailModal(null); setEmailInput(""); }} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="edit-email-cancel">Cancelar</button>
            <button onClick={saveClientEmail} disabled={emailSaving} className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white font-bold px-5 py-2 rounded-full text-sm" data-testid="edit-email-save">
              {emailSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Guardar
            </button>
          </div>
        </Modal>
      )}

      {/* Modal: editar datos de farmacia (nombre, teléfono, provincia) */}
      {pharmaModal && (
        <Modal onClose={() => setPharmaModal(null)} testid="edit-pharma-modal">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-bold mb-1">Editar datos de la farmacia</div>
          <div className="font-display text-2xl font-bold tracking-tight inline-flex items-center gap-2 mb-4">
            <Pencil className="h-5 w-5 text-sky-600" />
            {pharmaModal.code}
          </div>

          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Nombre de la farmacia</label>
          <input
            type="text"
            value={pharmaForm.pharmacy_name}
            onChange={(e) => setPharmaForm({ ...pharmaForm, pharmacy_name: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm mb-3"
            data-testid="edit-pharma-name"
          />

          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Teléfono</label>
          <input
            type="text"
            value={pharmaForm.phone}
            onChange={(e) => setPharmaForm({ ...pharmaForm, phone: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm mb-3"
            data-testid="edit-pharma-phone"
          />

          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Provincia</label>
          <select
            value={pharmaForm.province}
            onChange={(e) => setPharmaForm({ ...pharmaForm, province: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
            data-testid="edit-pharma-province"
          >
            <option value="">— Sin especificar —</option>
            {pharmaProvinces.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <div className="text-[11px] text-slate-500 mt-1">
            La comunidad autónoma se ajusta automáticamente al cambiar la provincia. Usa esto si asignaste la región equivocada por error.
          </div>

          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setPharmaModal(null)} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="edit-pharma-cancel">Cancelar</button>
            <button onClick={savePharmaDetails} disabled={pharmaSaving} className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white font-bold px-5 py-2 rounded-full text-sm" data-testid="edit-pharma-save">
              {pharmaSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Guardar
            </button>
          </div>
        </Modal>
      )}

      {/* Modal: generar PDF consultor */}
      {pdfModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-8 overflow-y-auto" onClick={() => setPdfModal(null)} data-testid="pdf-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setPdfModal(null)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-900" data-testid="pdf-modal-close">✕</button>
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-bold mb-1">Informe de consultoría</div>
            <div className="font-display text-2xl font-bold tracking-tight inline-flex items-center gap-2 mb-1">
              <FileText className="h-5 w-5 text-slate-900" />
              PDF · {pdfModal.code}
            </div>
            {pdfModal.pharmacy_name && (
              <div className="text-sm text-slate-500 mb-4">{pdfModal.pharmacy_name}</div>
            )}
            <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-sky-900 mb-4">
              El PDF incluye automáticamente: farmacia, código, provincia, % genérico (uds y €), evolución, moléculas SOLO MARCA, top 3 proveedores y tu firma.
              Los campos de abajo son <strong>opcionales</strong> — si los dejas vacíos, el PDF tendrá líneas en blanco para escribir a mano.
            </div>

            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Notas de la visita</label>
            <textarea
              value={pdfNotes}
              onChange={(e) => setPdfNotes(e.target.value)}
              placeholder="Ej: Reunión 12/06. Repasamos panel EFG. Sandoz al 27% del gasto — muy alto. Farmacéutica quiere probar Cinfa en ATORVASTATINA…"
              rows={4}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 mb-3"
              data-testid="pdf-notes"
            />

            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Recomendaciones y próximos pasos</label>
            <textarea
              value={pdfRecs}
              onChange={(e) => setPdfRecs(e.target.value)}
              placeholder="Ej: 1) Cambiar preferente ATORVASTATINA a Cinfa. 2) Firmar convenio Kern (10% vademecum). 3) Formar personal en 3 moléculas SOLO MARCA principales."
              rows={4}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 mb-3"
              data-testid="pdf-recommendations"
            />

            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Próxima visita (opcional)</label>
            <input
              type="text"
              value={pdfNextVisit}
              onChange={(e) => setPdfNextVisit(e.target.value)}
              placeholder="Ej: 12/07/2026 a las 10:30h"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
              data-testid="pdf-next-visit"
            />

            {/* Sección: enviar por email */}
            <div className="mt-5 pt-5 border-t border-slate-200">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={pdfSendEmail}
                  onChange={(e) => {
                    setPdfSendEmail(e.target.checked);
                    if (e.target.checked && !pdfRecipient && pdfModal?.email) {
                      setPdfRecipient(pdfModal.email);
                    }
                  }}
                  data-testid="pdf-send-email-toggle"
                  className="h-4 w-4 rounded accent-emerald-600"
                />
                <span className="text-sm font-semibold text-slate-800">
                  📧 Enviar por email a la farmacia (adjunta el PDF)
                </span>
              </label>

              {pdfSendEmail && (
                <div className="mt-4 space-y-3 pl-6">
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Destinatario</label>
                    <input
                      type="email"
                      value={pdfRecipient}
                      onChange={(e) => setPdfRecipient(e.target.value)}
                      placeholder={pdfModal?.email || "farmacia@ejemplo.com"}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                      data-testid="pdf-recipient-email"
                    />
                    {pdfModal?.email && !pdfRecipient && (
                      <div className="text-[11px] text-slate-500 mt-1">
                        Si lo dejas vacío, se enviará a <strong>{pdfModal.email}</strong>.
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Asunto (opcional)</label>
                    <input
                      type="text"
                      value={pdfCustomSubject}
                      onChange={(e) => setPdfCustomSubject(e.target.value)}
                      placeholder={`Informe de consultoría FarmaClass · ${pdfModal?.pharmacy_name || pdfModal?.code || ""}`}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                      data-testid="pdf-custom-subject"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Mensaje personalizado al cliente</label>
                    <textarea
                      value={pdfCustomMessage}
                      onChange={(e) => setPdfCustomMessage(e.target.value)}
                      placeholder="¡Enhorabuena! Este mes has mejorado un 3% tu grado de dispensación. Te dejo también un par de recomendaciones para seguir subiendo…"
                      rows={4}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                      data-testid="pdf-custom-message"
                    />
                    <div className="text-[11px] text-slate-500 mt-1">
                      Si lo dejas vacío, se enviará un mensaje estándar de acompañamiento.
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setPdfModal(null)} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="pdf-cancel">Cancelar</button>
              <button
                onClick={downloadConsultancyPdf}
                disabled={pdfDownloading || pdfSending}
                className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 text-slate-900 font-semibold px-5 py-2 rounded-full text-sm"
                data-testid="pdf-download"
              >
                {pdfDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Descargar
              </button>
              {pdfSendEmail && (
                <button
                  onClick={sendConsultancyPdf}
                  disabled={pdfSending || pdfDownloading}
                  className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold px-5 py-2 rounded-full text-sm"
                  data-testid="pdf-send-email"
                >
                  {pdfSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                  Enviar por email
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Estudios duplicados */}
      {showDuplicatesModal && duplicates && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 md:p-8 overflow-y-auto"
          onClick={() => setShowDuplicatesModal(false)}
          data-testid="duplicates-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-rose-100 flex items-center justify-center">
                  <AlertTriangle className="h-4 w-4 text-rose-700" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Estudios duplicados</h2>
                  <p className="text-xs text-slate-500">Consolida las dispensaciones que comparten periodo. Se recomienda conservar el estudio con desglose de empleados.</p>
                </div>
              </div>
              <button
                onClick={() => setShowDuplicatesModal(false)}
                className="text-slate-400 hover:text-slate-900"
                data-testid="duplicates-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-5 max-h-[75vh] overflow-y-auto">
              {duplicates.groups.length === 0 ? (
                <div className="text-center py-10 text-slate-500">
                  <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-500 mb-3" />
                  <div className="font-semibold text-slate-800">Sin duplicados</div>
                  <div className="text-sm">Ninguna farmacia tiene estudios repetidos en el mismo periodo.</div>
                </div>
              ) : duplicates.groups.map((g, gi) => (
                <div key={`${g.user_id}-${g.period_start}`} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`dup-group-${gi}`}>
                  <div className="bg-slate-50 px-4 py-3 flex flex-wrap items-center justify-between gap-2 border-b border-slate-200">
                    <div>
                      <div className="font-semibold text-slate-900 text-sm">
                        {g.pharmacy_name || g.email}
                        {g.license_code && <span className="ml-2 text-[10px] px-2 py-0.5 bg-slate-200 rounded font-mono-num">{g.license_code}</span>}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{g.email} · Periodo <span className="font-mono-num font-semibold">{g.period_start}</span> · {g.count} estudios</div>
                    </div>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {g.studies.map((s) => {
                      const isRecommended = s.id === g.recommended_keep_id;
                      return (
                        <div key={s.id} className={`flex items-center justify-between px-4 py-3 ${isRecommended ? "bg-emerald-50/50" : ""}`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-slate-900 text-sm truncate">{s.label || "(sin label)"}</span>
                              {isRecommended && (
                                <span className="text-[10px] px-2 py-0.5 bg-emerald-600 text-white rounded font-semibold">RECOMENDADO CONSERVAR</span>
                              )}
                              {s.has_employee_breakdown && (
                                <span className="text-[10px] px-2 py-0.5 bg-sky-100 text-sky-800 rounded font-semibold">Con desglose empleados</span>
                              )}
                              {!s.has_employee_breakdown && (
                                <span className="text-[10px] px-2 py-0.5 bg-amber-100 text-amber-800 rounded font-semibold">Sin desglose</span>
                              )}
                            </div>
                            <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-3">
                              <span>ID: <span className="font-mono-num">{s.id.slice(0, 8)}</span></span>
                              <span>{s.total_lines} líneas</span>
                              <span>Creado: {formatDate(s.created_at)}</span>
                            </div>
                          </div>
                          <button
                            onClick={() => deleteDuplicateStudy(s.id, s.label)}
                            disabled={duplicatesDeleting === s.id || isRecommended}
                            title={isRecommended ? "Este es el que recomendamos conservar. Bórralo sólo si estás seguro." : "Borrar este estudio"}
                            className={`ml-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium disabled:opacity-40 ${isRecommended ? "border border-slate-300 text-slate-500" : "bg-rose-600 hover:bg-rose-700 text-white"}`}
                            data-testid={`dup-delete-${s.id}`}
                          >
                            {duplicatesDeleting === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                            Borrar
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal: fecha de cobro / renovación anual */}
      {paidModal && (
        <div
          className="fixed inset-0 z-50 bg-slate-950/50 backdrop-blur-sm flex items-start justify-center p-4 sm:p-10 overflow-y-auto"
          onClick={() => setPaidModal(null)}
          data-testid="license-paid-modal"
        >
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl my-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 p-5 border-b border-slate-100">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-sky-700">Cobro y renovación</div>
                <h3 className="font-display text-xl font-extrabold tracking-tight text-slate-950 mt-0.5">
                  {paidModal.pharmacy || paidModal.code}
                </h3>
                <p className="text-xs text-slate-500 font-mono-num mt-0.5">{paidModal.code}</p>
              </div>
              <button onClick={() => setPaidModal(null)} className="text-slate-400 hover:text-slate-900" data-testid="license-paid-close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-slate-500">
                La licencia cubre <strong>1 año</strong> desde la fecha de cobro. Guardo la fecha y te aviso en la tabla
                cuando toque renovar.
              </p>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-1.5 block">Fecha de cobro</span>
                <input
                  type="date"
                  value={paidModal.date || ""}
                  onChange={(e) => setPaidModal((m) => ({ ...m, date: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-white text-slate-900 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  data-testid="license-paid-input"
                />
              </label>
              {paidModal.date && (
                <p className="text-xs text-slate-600">
                  Renovación prevista:{" "}
                  <strong className="font-mono-num" data-testid="license-paid-preview">
                    {formatDate(new Date(new Date(paidModal.date).setFullYear(new Date(paidModal.date).getFullYear() + 1)).toISOString())}
                  </strong>
                </p>
              )}
              <div className="flex items-center justify-between gap-2 pt-1">
                <button
                  onClick={() => savePaidDate(true)}
                  disabled={paidSaving}
                  className="text-xs font-semibold text-rose-600 hover:text-rose-800 disabled:opacity-50"
                  data-testid="license-paid-clear"
                >
                  Borrar fecha
                </button>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPaidModal(null)} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="license-paid-cancel">
                    Cancelar
                  </button>
                  <button
                    onClick={() => savePaidDate(false)}
                    disabled={paidSaving || !paidModal.date}
                    className="inline-flex items-center gap-2 bg-slate-950 text-white px-5 py-2.5 rounded-lg text-sm font-bold hover:bg-slate-800 disabled:opacity-60"
                    data-testid="license-paid-save"
                  >
                    {paidSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />}
                    Guardar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: nota interna del administrador sobre el cliente */}
      {noteModal && (
        <div
          className="fixed inset-0 z-50 bg-slate-950/50 backdrop-blur-sm flex items-start justify-center p-4 sm:p-10 overflow-y-auto"
          onClick={() => setNoteModal(null)}
          data-testid="license-note-modal"
        >
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl my-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 p-5 border-b border-slate-100">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-700">Nota interna</div>
                <h3 className="font-display text-xl font-extrabold tracking-tight text-slate-950 mt-0.5">
                  {noteModal.pharmacy || noteModal.code}
                </h3>
                <p className="text-xs text-slate-500 font-mono-num mt-0.5">{noteModal.code}</p>
              </div>
              <button onClick={() => setNoteModal(null)} className="text-slate-400 hover:text-slate-900" data-testid="license-note-close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-slate-500">
                Solo visible para administradores. Úsalo para anotar llamadas, acuerdos, incidencias o próximos pasos con este cliente.
              </p>
              <textarea
                value={noteModal.text || ""}
                onChange={(e) => setNoteModal((m) => ({ ...m, text: e.target.value }))}
                rows={8}
                autoFocus
                placeholder="Ej.: 12/06 llamada con Pedro — quiere pasar a suscripción anual. Pendiente enviar propuesta."
                className="w-full px-3 py-2.5 bg-white text-slate-900 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                data-testid="license-note-textarea"
              />
              {noteModal.updated_at && (
                <p className="text-[11px] text-slate-400">Última edición: {formatDate(noteModal.updated_at)}</p>
              )}
              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  onClick={() => setNoteModal(null)}
                  className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900"
                  data-testid="license-note-cancel"
                >
                  Cancelar
                </button>
                <button
                  onClick={saveNote}
                  disabled={noteSaving}
                  className="inline-flex items-center gap-2 bg-slate-950 text-white px-5 py-2.5 rounded-lg text-sm font-bold hover:bg-slate-800 disabled:opacity-60"
                  data-testid="license-note-save"
                >
                  {noteSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <StickyNote className="h-4 w-4" />}
                  Guardar nota
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Modal: Informe de mejoras (release notes) */}
      {showReleaseModal && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 md:p-8 overflow-y-auto"
          onClick={() => !releaseSending && setShowReleaseModal(false)}
          data-testid="release-notes-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-sky-100 flex items-center justify-center">
                  <Megaphone className="h-4 w-4 text-sky-700" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Informe de mejoras</h2>
                  <p className="text-xs text-slate-500">Envía a los clientes las últimas novedades de la plataforma.</p>
                </div>
              </div>
              <button
                onClick={() => !releaseSending && setShowReleaseModal(false)}
                className="text-slate-400 hover:text-slate-900"
                data-testid="release-notes-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid md:grid-cols-5 gap-0 max-h-[75vh] overflow-hidden">
              {/* Left: controles */}
              <div className="md:col-span-2 p-6 border-r border-slate-200 space-y-5 overflow-y-auto max-h-[75vh]">
                <div>
                  <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2 block">Destinatarios</label>
                  <div className="space-y-2">
                    {[
                      { v: "test", l: "Prueba (un email)" },
                      { v: "active_users", l: "Usuarios en uso (active/activated)" },
                      { v: "all_licensees", l: "Todos los usuarios con licencia" },
                    ].map((opt) => (
                      <label key={opt.v} className={`flex items-center gap-2 p-3 rounded-md border cursor-pointer text-sm ${releaseScope === opt.v ? "border-sky-400 bg-sky-50" : "border-slate-200 hover:bg-slate-50"}`}>
                        <input
                          type="radio"
                          name="release-scope"
                          value={opt.v}
                          checked={releaseScope === opt.v}
                          onChange={() => { setReleaseScope(opt.v); refreshRecipients(opt.v); }}
                          data-testid={`release-scope-${opt.v}`}
                        />
                        <span>{opt.l}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {releaseScope === "test" && (
                  <div>
                    <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2 block">Email de prueba</label>
                    <input
                      type="email"
                      value={releaseTestEmail}
                      onChange={(e) => setReleaseTestEmail(e.target.value)}
                      placeholder="tu@email.com"
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                      data-testid="release-test-email"
                    />
                  </div>
                )}

                {releaseScope !== "test" && releaseRecipients && (
                  <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs">
                    <div className="font-semibold text-slate-700 mb-1">
                      {releaseRecipients.count} destinatario{releaseRecipients.count === 1 ? "" : "s"}
                    </div>
                    {releaseRecipients.sample?.length ? (
                      <div className="text-slate-500 truncate">Ej.: {releaseRecipients.sample.join(", ")}…</div>
                    ) : null}
                  </div>
                )}

                <div>
                  <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2 block">Introducción personalizada (opcional)</label>
                  <textarea
                    value={releaseCustomIntro}
                    onChange={(e) => setReleaseCustomIntro(e.target.value)}
                    rows={4}
                    placeholder="Deja vacío para usar el texto por defecto…"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                    data-testid="release-custom-intro"
                  />
                </div>

                <div className="pt-2 space-y-2">
                  {/* Iter-74: info del último envío + toggle "sólo novedades" */}
                  {releaseLastSent && (
                    <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs space-y-1.5">
                      {releaseLastSent.sent_at ? (
                        <>
                          <div className="font-semibold text-sky-900">
                            Último envío: {new Date(releaseLastSent.sent_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" })}
                            {releaseLastSent.sent_up_to_version ? ` · hasta v${releaseLastSent.sent_up_to_version}` : ""}
                          </div>
                          <div className="text-sky-800">
                            <strong className={releaseLastSent.new_versions_count > 0 ? "text-emerald-700" : "text-slate-500"}>
                              {releaseLastSent.new_versions_count}
                            </strong>{" "}
                            mejora{releaseLastSent.new_versions_count === 1 ? "" : "s"} nueva{releaseLastSent.new_versions_count === 1 ? "" : "s"} desde entonces (última: v{releaseLastSent.current_latest_version})
                          </div>
                        </>
                      ) : (
                        <div className="font-semibold text-sky-900">
                          Primer envío: se incluirán las {releaseLastSent.new_versions_count} versiones curadas
                        </div>
                      )}
                      <label className="flex items-center gap-2 pt-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={releaseOnlyNew}
                          onChange={(e) => setReleaseOnlyNew(e.target.checked)}
                          className="h-3.5 w-3.5"
                          data-testid="release-only-new-toggle"
                        />
                        <span className="text-sky-900">Enviar sólo novedades desde el último envío</span>
                      </label>
                      {/* Iter-75: date-picker para acotar por fecha */}
                      <div className="pt-2 border-t border-sky-200 mt-2">
                        <label className="text-[10px] font-bold text-sky-900 uppercase tracking-wider block mb-1">
                          Fecha desde (opcional)
                        </label>
                        <div className="flex gap-2 items-center">
                          <input
                            type="date"
                            value={releaseSinceDate}
                            onChange={(e) => setReleaseSinceDate(e.target.value)}
                            className="flex-1 px-2 py-1.5 border border-sky-300 rounded text-xs bg-white"
                            data-testid="release-since-date"
                          />
                          {releaseSinceDate && (
                            <button
                              onClick={() => setReleaseSinceDate("")}
                              className="text-[10px] text-sky-700 underline hover:text-sky-900"
                              data-testid="release-since-date-clear"
                            >
                              limpiar
                            </button>
                          )}
                        </div>
                        <div className="text-[10px] text-sky-800 mt-1">
                          Sólo se enviarán mejoras publicadas en esa fecha o posteriores.
                        </div>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => sendReleaseNotes(true)}
                    disabled={releaseSending}
                    className="w-full inline-flex items-center justify-center gap-2 border border-slate-300 text-slate-800 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
                    data-testid="release-dry-run"
                  >
                    {releaseSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                    Vista previa (sin enviar)
                  </button>
                  <button
                    onClick={() => sendReleaseNotes(false)}
                    disabled={releaseSending || (releaseScope === "test" && !releaseTestEmail.trim())}
                    className="w-full inline-flex items-center justify-center gap-2 bg-slate-950 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
                    data-testid="release-send"
                  >
                    {releaseSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Enviar informe
                  </button>
                </div>

                {releaseResult && !releaseResult.dry_run && (
                  <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-md p-3 text-xs">
                    <div className="font-semibold">
                      Enviado a {releaseResult.sent}/{releaseResult.recipients_count} destinatarios
                    </div>
                    {releaseResult.failed > 0 && (
                      <div className="mt-1 text-amber-800">Fallidos: {releaseResult.failed}</div>
                    )}
                  </div>
                )}
              </div>

              {/* Right: preview HTML */}
              <div className="md:col-span-3 p-6 bg-slate-100 overflow-y-auto max-h-[75vh]">
                <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Vista previa del email</div>
                  <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer bg-white border border-slate-200 rounded-full px-3 py-1 hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={!!releasePreview?.include_admin}
                      onChange={(e) => toggleAdminView(e.target.checked)}
                      className="h-3 w-3"
                      data-testid="release-toggle-admin"
                    />
                    Ver también mejoras de admin (no se envían al cliente)
                  </label>
                </div>
                {releaseLoading ? (
                  <div className="flex items-center justify-center py-16 text-slate-400">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Cargando…
                  </div>
                ) : releasePreview?.preview_html ? (
                  <div
                    className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden"
                    data-testid="release-preview-html"
                    dangerouslySetInnerHTML={{ __html: releasePreview.preview_html }}
                  />
                ) : (
                  <div className="text-slate-400 text-sm">Sin preview disponible.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Fecha de cobro + aviso de renovación (la licencia cubre 1 año). */
function RenewalCell({ license, onEdit }) {
  const paid = (license.paid_at || "").slice(0, 10);
  const days = license.days_to_renewal;
  if (!paid) {
    return (
      <button
        onClick={onEdit}
        className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-400 hover:text-slate-800 border border-dashed border-slate-300 hover:border-slate-500 rounded-md px-2 py-1"
        data-testid={`renewal-set-${license.code}`}
      >
        <CalendarClock className="h-3.5 w-3.5" /> Fecha de cobro
      </button>
    );
  }
  const cls =
    days == null ? "text-slate-500"
      : days < 0 ? "text-rose-700 font-bold"
        : days <= 30 ? "text-rose-700 font-bold"
          : days <= 60 ? "text-amber-700 font-bold"
            : "text-slate-600";
  const msg =
    days == null ? ""
      : days < 0 ? `Vencida hace ${Math.abs(days)} d`
        : days === 0 ? "Renueva HOY"
          : `Renovar en ${days} d`;
  return (
    <button onClick={onEdit} className="text-left group" data-testid={`renewal-edit-${license.code}`} title="Editar fecha de cobro">
      <div className="text-slate-800 group-hover:underline">
        Cobro: {formatDate(license.paid_at)}
      </div>
      <div className={`text-[11px] ${cls}`}>
        {formatDate(license.renews_at)}{msg ? ` · ${msg}` : ""}
      </div>
    </button>
  );
}


function BillingCell({ license, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(license.billed_amount_eur ?? "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setAmount(license.billed_amount_eur ?? ""); }, [license.billed_amount_eur]);

  const l = license;
  const hasAmount = l.billed_amount_eur != null && l.billed_amount_eur > 0;

  const patch = async (extra) => {
    setSaving(true);
    try { await onUpdate(extra); } finally { setSaving(false); }
  };

  const saveAmount = async () => {
    const num = Number(String(amount).replace(",", "."));
    if (isNaN(num) || num < 0) { toast.error("Importe inválido"); return; }
    await patch({ billed_amount_eur: num, billed: num > 0 ? true : l.billed });
  };

  return (
    <div className="relative inline-block" data-testid={`billing-cell-${l.code}`}>
      <button
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold border transition-colors ${
          l.paid ? "bg-emerald-600 text-white border-emerald-700"
            : l.billed ? "bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200"
            : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
        }`}
        title={l.paid ? "Cobrada" : l.billed ? "Facturada · pendiente de cobro" : "Sin facturar"}
      >
        {l.paid ? "✓ Cobrada" : l.billed ? "📄 Facturada" : "○ Sin facturar"}
        {hasAmount && (
          <span className="ml-1 font-mono-num opacity-90">{fmtEur(l.billed_amount_eur)}</span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div
            className="absolute z-30 right-0 mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-left"
            onClick={(e) => e.stopPropagation()}
            data-testid={`billing-popover-${l.code}`}
          >
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Facturación · {l.code}</div>

            <label className="flex items-center gap-2 py-1 cursor-pointer">
              <input
                type="checkbox"
                checked={!!l.billed}
                onChange={(e) => patch({ billed: e.target.checked })}
                disabled={saving}
                className="h-4 w-4 accent-amber-600 rounded"
                data-testid={`billed-toggle-${l.code}`}
              />
              <span className="text-sm">
                Facturada
                {l.billed && l.billed_at && (
                  <span className="text-[10px] text-slate-500 ml-1">
                    ({new Date(l.billed_at).toLocaleDateString("es-ES")})
                  </span>
                )}
              </span>
            </label>

            <div className="py-1">
              <label className="text-xs text-slate-600 block mb-1">Importe facturado</label>
              <div className="flex gap-1">
                <input
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0,00"
                  className="w-full px-2 py-1 border border-slate-300 rounded text-sm text-right font-mono-num"
                  data-testid={`billed-amount-${l.code}`}
                />
                <span className="inline-flex items-center px-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded">€</span>
                <button
                  onClick={saveAmount}
                  disabled={saving}
                  className="px-2 py-1 text-xs font-bold rounded bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-50"
                  data-testid={`billed-amount-save-${l.code}`}
                >Guardar</button>
              </div>
            </div>

            <label className="flex items-center gap-2 py-1 cursor-pointer mt-1 border-t border-slate-100 pt-2">
              <input
                type="checkbox"
                checked={!!l.paid}
                onChange={(e) => patch({ paid: e.target.checked })}
                disabled={saving}
                className="h-4 w-4 accent-emerald-600 rounded"
                data-testid={`paid-toggle-${l.code}`}
              />
              <span className="text-sm font-semibold text-emerald-800">
                Cobrada
                {l.paid && l.paid_at && (
                  <span className="text-[10px] text-slate-500 ml-1 font-normal">
                    ({new Date(l.paid_at).toLocaleDateString("es-ES")})
                  </span>
                )}
              </span>
            </label>
          </div>
        </>
      )}
    </div>
  );
}

function Modal({ children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-8 overflow-y-auto" onClick={onClose} data-testid={testid}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-900" data-testid={`${testid}-close`}>✕</button>
        {children}
      </div>
    </div>
  );
}

function Stat({ label, value, cls = "text-slate-900" }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1">{label}</div>
      <div className={`font-display text-2xl font-bold font-mono-num ${cls}`}>{value}</div>
    </div>
  );
}

function CashStat({ label, value, sub, cls = "text-slate-900", testid, onClick }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      data-testid={testid}
      className={`bg-white border border-slate-200 rounded-lg p-4 text-left ${
        onClick ? "hover:border-slate-400 transition-colors cursor-pointer" : ""
      }`}
    >
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1">{label}</div>
      <div className={`font-display text-3xl font-bold font-mono-num tabular-nums ${cls}`}>
        {Math.round(value).toLocaleString("es-ES")} €
      </div>
      <div className="text-xs text-slate-500 mt-1">{sub}</div>
    </Tag>
  );
}


const InactivityBadge = ({ days, code }) => {
  if (days == null) return null;
  const cls = days >= 60
    ? "bg-rose-100 text-rose-700 border-rose-200"
    : days >= INACTIVITY_THRESHOLD_DAYS
      ? "bg-amber-100 text-amber-800 border-amber-200"
      : "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span
      className={`inline-block mt-0.5 px-1.5 py-0.5 rounded border text-[10px] font-bold ${cls}`}
      data-testid={`lic-inactive-days-${code}`}
    >
      {days === 0 ? "hoy" : `${days} días sin entrar`}
    </span>
  );
};

function formatDateTime(iso) {  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
}

function fmtEur(n) {
  const v = Number(n) || 0;
  return v.toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
}

function TrendBadge({ delta, label }) {
  const v = Number(delta) || 0;
  if (Math.abs(v) < 0.05) {
    return (
      <span className="inline-flex items-center gap-1 text-slate-500 font-mono-num text-[11px]" title={label}>
        <Minus className="h-3 w-3" /> igual
      </span>
    );
  }
  if (v > 0) {
    return (
      <span className="inline-flex items-center gap-1 text-emerald-700 font-mono-num text-[11px] font-bold" title={label}>
        <TrendingUp className="h-3 w-3" /> +{v.toFixed(1)}pp
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-rose-700 font-mono-num text-[11px] font-bold" title={label}>
      <TrendingDown className="h-3 w-3" /> {v.toFixed(1)}pp
    </span>
  );
}

function ConsultancySummary({ state, onGeneratePdf }) {
  if (!state || state.loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-slate-500 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando resumen de consultoría…
      </div>
    );
  }
  if (state.error) {
    return <div className="p-6 text-rose-600 text-sm">Error: {state.error}</div>;
  }
  const d = state.data;
  if (!d) return null;

  if (!d.user_id) {
    return (
      <div className="p-6 text-sm text-slate-500 italic">
        Licencia sin activar todavía · sin datos del cliente.
      </div>
    );
  }

  const ls = d.last_study;
  const noStudy = !ls;
  const t25 = d.top25;

  return (
    <div className="p-6 border-t border-slate-200 bg-white" data-testid="consultancy-summary">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Resumen de consultoría</div>
        <button
          onClick={onGeneratePdf}
          className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-700 text-white text-xs font-bold px-3 py-1.5 rounded-md"
          data-testid="btn-consultancy-pdf"
          title="Descarga un PDF con estos datos + tus notas/recomendaciones para el cliente"
        >
          <FileText className="h-3.5 w-3.5" /> PDF consultor
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
          <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Estudios cargados</div>
          <div className="text-sm font-medium text-slate-900 mt-1 font-mono-num">{d.studies_count} <span className="text-slate-400 text-xs">meses</span></div>
          <div className="text-[10px] text-slate-500 mt-0.5">Último: {ls?.label || "—"}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">{d.login_count || 0} logins totales</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <div className="text-[9px] uppercase tracking-widest text-emerald-700 font-bold">% Genérico · unidades</div>
          <div className="text-lg font-bold text-emerald-800 mt-0.5 font-mono-num">{noStudy ? "—" : `${ls.generic_pct_units.toFixed(1)}%`}</div>
          {d.trend && <TrendBadge delta={d.trend.delta_pct_units} label={`vs ${d.trend.prev_label}: ${d.trend.prev_generic_pct_units.toFixed(1)}%`} />}
        </div>
        <div className="bg-sky-50 border border-sky-200 rounded-lg p-3">
          <div className="text-[9px] uppercase tracking-widest text-sky-700 font-bold">% Genérico · euros</div>
          <div className="text-lg font-bold text-sky-800 mt-0.5 font-mono-num">{noStudy ? "—" : `${ls.generic_pct_eur.toFixed(1)}%`}</div>
          {d.trend && <TrendBadge delta={d.trend.delta_pct_eur} label={`vs ${d.trend.prev_label}: ${d.trend.prev_generic_pct_eur.toFixed(1)}%`} />}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Stock gap */}
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-2 inline-flex items-center gap-1.5">
            <AlertTriangle className="h-3 w-3 text-rose-500" />
            Moléculas SOLO MARCA (oportunidad)
          </div>
          {d.stock_gap && d.stock_gap.total_gh > 0 ? (
            <div>
              <div className="text-2xl font-bold text-rose-700 font-mono-num">
                {d.stock_gap.only_marca_pct.toFixed(1)}%
              </div>
              <div className="text-xs text-slate-600 mt-1">
                <span className="font-mono-num font-bold">{d.stock_gap.only_marca_gh}</span> de{" "}
                <span className="font-mono-num font-bold">{d.stock_gap.total_gh}</span> moléculas dispensadas están en marca sin genérico.
              </div>
              <div className="text-[10px] text-slate-500 mt-1.5">Estudio base: {ls?.label}</div>
            </div>
          ) : <div className="text-slate-400 text-sm">Sin datos de estudio.</div>}
        </div>

        {/* Iter-138 · Top 25: % del conjunto + evolución + las 3 primeras moléculas */}
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="top25-summary-card">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">
              % Genérico del Top 25
            </div>
            {t25?.prev_label && (
              <div className="text-[10px] text-slate-400">vs {t25.prev_label}</div>
            )}
          </div>
          {t25 && t25.n > 0 ? (
            <div>
              <div className="flex items-end gap-2">
                <div className="text-2xl font-bold text-indigo-700 font-mono-num" data-testid="top25-gen-pct">
                  {t25.gen_pct_units.toFixed(1)}%
                </div>
                {t25.delta_pp !== null && t25.delta_pp !== undefined && (
                  <span
                    className={`inline-flex items-center gap-0.5 text-xs font-bold font-mono-num pb-1 ${
                      t25.direction === "up" ? "text-emerald-600" : t25.direction === "down" ? "text-rose-600" : "text-slate-400"
                    }`}
                    data-testid="top25-trend"
                    title={`Antes: ${t25.prev_gen_pct_units?.toFixed(1)}% · ahora: ${t25.gen_pct_units.toFixed(1)}%`}
                  >
                    {t25.direction === "up" ? <ArrowUpRight className="w-3.5 h-3.5" /> : t25.direction === "down" ? <ArrowDownRight className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
                    {t25.delta_pp > 0 ? "+" : ""}{t25.delta_pp.toFixed(1)} pp
                  </span>
                )}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                {t25.n} moléculas · {(t25.units || 0).toLocaleString("es-ES")} uds ·{" "}
                {fmtEur(t25.marca_eur || 0)} aún en marca
              </div>
              <div className="mt-2.5 pt-2 border-t border-slate-100 space-y-1.5">
                {(t25.first3 || []).map((m) => {
                  const up = m.direction === "up";
                  const down = m.direction === "down";
                  const Icon = up ? ArrowUpRight : down ? ArrowDownRight : Minus;
                  const color = up ? "text-emerald-600" : down ? "text-rose-600" : "text-slate-400";
                  return (
                    <div
                      key={m.gh}
                      className="flex items-center justify-between text-xs"
                      data-testid={`top25-mol-${m.rank}`}
                      title={m.prev_gen_pct_units === null || m.prev_gen_pct_units === undefined
                        ? `${m.gh}: sin dato del estudio anterior`
                        : `${m.gh}: ${m.prev_gen_pct_units}% → ${m.gen_pct_units}%`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-[10px] font-bold text-slate-400 font-mono-num w-4">#{m.rank}</span>
                        <span className="truncate font-medium text-slate-800">{m.gh}</span>
                      </div>
                      <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                        <span className="font-mono-num font-bold text-slate-900">{m.gen_pct_units.toFixed(1)}%</span>
                        <span className={`flex items-center gap-0.5 font-mono-num text-[10px] font-bold w-[52px] justify-end ${color}`}>
                          <Icon className="w-3 h-3" />
                          {m.delta_pp === null || m.delta_pp === undefined ? "nuevo" : `${m.delta_pp > 0 ? "+" : ""}${m.delta_pp.toFixed(1)}`}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : <div className="text-slate-400 text-sm">Sin datos de estudio.</div>}
        </div>
      </div>

      {/* Iter-137 · Laboratorios de GENÉRICOS con flecha vs estudio anterior */}
      <div className="mt-3 bg-white border border-slate-200 rounded-lg p-4" data-testid="generic-suppliers-card">
        <div className="flex items-baseline justify-between gap-3 mb-2">
          <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold flex-shrink-0">
            Laboratorios de genéricos · último estudio
          </div>
          <div className="text-[10px] text-slate-400 truncate min-w-0 text-right">
            {d.generic_suppliers_meta?.has_prev
              ? `flecha = uds/mes vs ${d.generic_suppliers_meta.prev_label}`
              : "sin estudio anterior para comparar"}
          </div>
        </div>
        {d.generic_suppliers && d.generic_suppliers.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-x-10 gap-y-1.5">
            {d.generic_suppliers.map((s, i) => {
              const up = s.direction === "up";
              const down = s.direction === "down";
              const isNew = s.direction === "new";
              const color = up ? "text-emerald-600" : down ? "text-rose-600" : isNew ? "text-sky-600" : "text-slate-400";
              const Icon = up ? ArrowUpRight : down ? ArrowDownRight : isNew ? Sparkles : Minus;
              return (
                <div
                  key={s.laboratorio}
                  className="flex items-center justify-between text-sm"
                  data-testid={`generic-supplier-row-${i}`}
                  title={
                    isNew
                      ? `${s.laboratorio}: nuevo laboratorio de genéricos en este estudio (${s.units_pm} uds/mes)`
                      : `${s.laboratorio}: ${s.prev_units_pm ?? "—"} → ${s.units_pm} uds/mes`
                  }
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[10px] font-bold text-slate-400 font-mono-num w-4">#{i + 1}</span>
                    <span className="truncate font-medium text-slate-800">{s.laboratorio}</span>
                  </div>
                  <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                    <span className="font-mono-num font-bold text-slate-900">{fmtEur(s.eur)}</span>
                    <span className="text-[10px] text-slate-500 font-mono-num">({s.share_pct}%)</span>
                    <span className={`flex items-center gap-0.5 font-mono-num text-[11px] font-bold w-[66px] justify-end ${color}`}>
                      <Icon className="w-3.5 h-3.5" />
                      {isNew ? "nuevo" : (s.delta_pct === null || s.delta_pct === undefined) ? "—" : `${s.delta_pct > 0 ? "+" : ""}${s.delta_pct}%`}
                    </span>
                  </div>
                </div>
              );
            })}
            <div className="md:col-span-2 text-[10px] text-slate-500 pt-1.5 border-t border-slate-100 mt-1.5">
              {d.generic_suppliers_meta?.n_labs} laboratorios de genéricos ·{" "}
              {(d.generic_suppliers_meta?.total_units || 0).toLocaleString("es-ES")} uds ·{" "}
              {fmtEur(d.generic_suppliers_meta?.total_eur || 0)}
            </div>
          </div>
        ) : <div className="text-slate-400 text-sm">Sin genéricos dispensados en el último estudio.</div>}
      </div>

      {/* Evolución detalle si hay tendencia */}
      {d.trend && (
        <div className="mt-3 bg-slate-900 text-slate-100 rounded-lg p-3 text-xs">
          <div className="text-[9px] uppercase tracking-widest text-slate-400 font-bold mb-1">Evolución vs estudio anterior ({d.trend.prev_label})</div>
          <div className="flex flex-wrap gap-6">
            <div>Unidades: <span className="font-mono-num font-bold">{d.trend.prev_generic_pct_units.toFixed(1)}%</span> → <span className="font-mono-num font-bold">{ls.generic_pct_units.toFixed(1)}%</span></div>
            <div>Euros: <span className="font-mono-num font-bold">{d.trend.prev_generic_pct_eur.toFixed(1)}%</span> → <span className="font-mono-num font-bold">{ls.generic_pct_eur.toFixed(1)}%</span></div>
          </div>
        </div>
      )}
    </div>
  );
}
